import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import SpecificEmpresaRequiredNotice from "../../../components/SpecificEmpresaRequiredNotice";
import { useCompanies } from "../../../hooks/companies";
import { useToast } from "../../../hooks/toast";
import api from "../../../services/api";
import { enviarAnexos, mensagemErroAnexo } from "../../../services/freteTabelaAnexosApi";
import AnexosTabela from "../TabelasFrete/AnexosTabela";
import EscolherArquivos from "components/EscolherArquivos";

const cardStyle = {
  boxShadow: "0px 1px 20px 0px rgb(0,0,0)",
};

const panelStyle = {
  background: "#1b2238",
  border: "1px solid #2b3553",
  borderRadius: 10,
  padding: "1rem 1.1rem",
  height: "100%",
};

const sectionTitleStyle = {
  color: "#f5f7fb",
  fontSize: "0.92rem",
  fontWeight: 600,
  marginBottom: "0.5rem",
};

const bodyTextStyle = {
  color: "#aab6dd",
  fontSize: "0.84rem",
  lineHeight: 1.55,
  marginBottom: 0,
};

const fieldLabelStyle = {
  color: "#8f9bc7",
  fontSize: "0.72rem",
  marginBottom: "0.35rem",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const compactInputStyle = {
  background: "#131c30",
  borderColor: "#2b3553",
  color: "#dbe7ff",
  fontSize: "0.82rem",
  minHeight: 38,
  lineHeight: 1.2,
  paddingTop: "0.5rem",
  paddingBottom: "0.5rem",
};

const TAXA_KEYS = ["ad_valorem", "gris", "seguro_obrigatorio", "pedagio"];
const CUSTO_KEYS = ["ate_75_kg", "de_76_a_100_kg", "acima_100_kg"];
const ROUTE_ADJUST_KEYS = [...CUSTO_KEYS, "prazo_entrega"];

const VALID_UFS = new Set([
  "AC","AL","AM","AP","BA","CE","DF","ES","GO","MA","MG","MS","MT",
  "PA","PB","PE","PI","PR","RJ","RN","RO","RR","RS","SC","SE","SP","TO",
]);

const COL_SHORT = {
  ate_75_kg: "≤75kg",
  de_76_a_100_kg: "76-100",
  acima_100_kg: ">100",
  ad_valorem: "Ad Val",
  seguro_obrigatorio: "Seg",
  pedagio: "Pedágio",
  gris: "GRIS",
  prazo_entrega: "Prazo",
};

const DEFAULT_WEIGHT_RANGE_PREVIEW = {
  ate_75_kg: { label: "Ate 75 kg", valUnidadeInicial: 0, valUnidadeFinal: 75 },
  de_76_a_100_kg: { label: "De 76 a 100 kg", valUnidadeInicial: 76, valUnidadeFinal: 100 },
  acima_100_kg: { label: "Acima de 100 kg", valUnidadeInicial: 101, valUnidadeFinal: null },
};

// PO1-3916: unidade "PcFx" = percentual sobre o valor da NF, por faixa de valor
// da NF (R$). As faixas padrão em kg acima NÃO valem: quando o documento não
// trouxe os limites, a faixa fica vazia e o usuário preenche na tabela.
const UNIDADE_PCFX = "PcFx";
const PCFX_COLUMN_LABELS = {
  ate_75_kg: "Faixa 1 (valor da NF)",
  de_76_a_100_kg: "Faixa 2 (valor da NF)",
  acima_100_kg: "Faixa 3 (valor da NF)",
};

// PO1-3148: terceiro modo, por unidade sobre o peso total (Custo ÷ divisor × peso).
// Aqui o modo é por COLUNA de faixa (a tela é assim); o backend expande para linha.
const COLUMN_CALCULATION_MODE_LABELS = {
  fixed: "Fixo",
  excess_kg: "Exced.",
  per_unit_total: "Por total",
};
const COLUMN_CALCULATION_MODES = ["fixed", "excess_kg", "per_unit_total"];
const DEFAULT_COLUMN_DIVISOR = 1000;

const MATCH_TOKEN_STOPWORDS = new Set([
  "a",
  "ao",
  "as",
  "com",
  "da",
  "das",
  "de",
  "do",
  "dos",
  "e",
  "em",
  "fx",
  "na",
  "no",
  "ou",
  "para",
  "por",
  "sem",
  "taxa",
]);

function parseWeightBoundary(value) {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  const parsed = Number(String(value).replace(",", "."));
  return Number.isNaN(parsed) ? null : parsed;
}

// `opts.pcFx` (PO1-3916): sem as faixas padrão em kg; rótulo neutro por faixa.
function getColumnDefinition(columnsExpected, key, opts = {}) {
  const found = (columnsExpected || []).find((column) => column.key === key);
  if (found) {
    return found;
  }

  if (opts.pcFx && PCFX_COLUMN_LABELS[key]) {
    return {
      key,
      label: PCFX_COLUMN_LABELS[key],
      weightRange: null,
      calculationMode: "fixed",
    };
  }

  const fallbackRange = DEFAULT_WEIGHT_RANGE_PREVIEW[key];
  if (fallbackRange) {
    return {
      key,
      label: fallbackRange.label,
      weightRange: {
        min: fallbackRange.valUnidadeInicial,
        max: fallbackRange.valUnidadeFinal,
      },
      calculationMode: "fixed",
    };
  }

  return {
    key,
    label: COL_SHORT[key] || key,
    weightRange: null,
    calculationMode: null,
  };
}

function getColumnLabel(columnsExpected, key, opts = {}) {
  const column = getColumnDefinition(columnsExpected, key, opts);
  if (column?.label) return column.label;
  if (opts.pcFx && PCFX_COLUMN_LABELS[key]) return PCFX_COLUMN_LABELS[key];
  return COL_SHORT[key] || key;
}

function getColumnWeightRange(columnsExpected, key, opts = {}) {
  const column = getColumnDefinition(columnsExpected, key, opts);
  // PO1-3916: PcFx não herda a faixa padrão em kg (fica em branco p/ preencher).
  const fallbackRange = opts.pcFx ? null : DEFAULT_WEIGHT_RANGE_PREVIEW[key];
  const min = parseWeightBoundary(column?.weightRange?.min);
  const max = parseWeightBoundary(column?.weightRange?.max);

  if (min !== null || max !== null) {
    return {
      valUnidadeInicial: min,
      valUnidadeFinal: max,
    };
  }

  return fallbackRange
    ? {
        valUnidadeInicial: fallbackRange.valUnidadeInicial,
        valUnidadeFinal: fallbackRange.valUnidadeFinal,
      }
    : {
        valUnidadeInicial: null,
        valUnidadeFinal: null,
      };
}

function normalizeSemanticToken(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toUpperCase();
}

function inferSuggestedValorRegra({ columnKey = null, freteTaxa = null } = {}) {
  const normalizedColumnKey = String(columnKey || "").trim().toLowerCase();
  const combinedText = [
    freteTaxa?.nome,
    freteTaxa?.descricao,
    freteTaxa?.codigo,
  ]
    .map(normalizeSemanticToken)
    .filter(Boolean)
    .join(" ");

  const suggestsPedagioPorPeso =
    combinedText.includes("PEDAG") &&
    (
      combinedText.includes("KG") ||
      combinedText.includes("100") ||
      combinedText.includes("FRAC")
    );

  if (suggestsPedagioPorPeso || normalizedColumnKey === "pedagio") {
    return 100;
  }

  const tipo = normalizeSemanticToken(freteTaxa?.tipo);
  const por = normalizeSemanticToken(freteTaxa?.por);

  if (tipo === "PERCENTUAL") {
    return 100;
  }

  if (tipo === "FIXO" && (por === "NF" || por === "CTE")) {
    return 0;
  }

  return 0;
}

function createClientRequestId() {
  return `ocr-save-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function buildSavePayloadSignature(payload) {
  try {
    return JSON.stringify(payload);
  } catch (error) {
    return `ocr-save-fallback-${Date.now()}`;
  }
}

function isSaveInProgressResponse(error, message) {
  const backendCode = error?.response?.data?.code;
  const status = error?.response?.status;
  const normalizedMessage = String(message || "").toLowerCase();

  return backendCode === "save_in_progress"
    || (status === 409 && normalizedMessage.includes("em andamento"));
}

function isRequestTimeoutLikeError(error, message) {
  const normalizedMessage = String(message || "").toLowerCase();
  const errorCode = String(error?.code || "").toLowerCase();

  return normalizedMessage.includes("timeout")
    || normalizedMessage.includes("request failed to complete")
    || normalizedMessage.includes("network error")
    || errorCode === "econnaborted";
}

function getInteriorTier(route) {
  const label = String(route.routeLabel || "");
  if (/2\s*$/.test(label)) return "INTERIOR-2";
  return "INTERIOR-1";
}

function inferDestinationType(route) {
  const label = String(route?.routeLabel || route?.descricao || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

  if (route?.destinationType === "capital" || label.includes("capital")) {
    return "capital";
  }

  return "interior";
}

function buildEditableRoutes(tableModel) {
  if (!tableModel || !Array.isArray(tableModel.routes)) return [];
  const cols = (tableModel.columnsExpected || []).map((c) => c.key);
  return tableModel.routes.map((route) => {
    const values = {};
    cols.forEach((key) => {
      const fl = (route.flowLines || []).find((l) => l.columnKey === key);
      if (fl && fl.parsedValue !== null && fl.parsedValue !== undefined) {
        values[key] = String(fl.parsedValue);
      } else if (route.columnMap && route.columnMap[key] !== null && route.columnMap[key] !== undefined) {
        values[key] = String(route.columnMap[key]);
      } else if (route.values && route.values[key] !== null && route.values[key] !== undefined) {
        values[key] = String(route.values[key]);
      } else {
        values[key] = "";
      }
    });
    return {
      routeIndex: route.routeIndex,
      ufDestino: route.ufDestino || "",
      routeLabel: route.routeLabel || "",
      destinationType: route.destinationType || inferDestinationType(route),
      values,
    };
  });
}

function getColumnMedian(columnKey, editableRoutes) {
  const vals = editableRoutes
    .map((r) => parseFloat(r.values[columnKey]))
    .filter((v) => !isNaN(v) && v > 0);
  if (vals.length === 0) return null;
  vals.sort((a, b) => a - b);
  return vals[Math.floor(vals.length / 2)];
}

function isSuspiciousValue(columnKey, rawValue, editableRoutes) {
  if (rawValue === "" || rawValue === null || rawValue === undefined) return false;
  const num = parseFloat(rawValue);
  if (isNaN(num)) return false;
  const currencyCols = ["ate_75_kg", "de_76_a_100_kg", "acima_100_kg", "pedagio"];
  if (currencyCols.includes(columnKey)) {
    const median = getColumnMedian(columnKey, editableRoutes);
    if (median !== null && median > 0 && num > median * 3) return true;
  }
  return false;
}

const emptyReviewForm = {
  transportadoraNome: "",
  transportadoraCnpj: "",
  clienteNome: "",
  clienteCnpj: "",
  tituloTabela: "",
  vigenciaInicio: "",
  vigenciaFim: "",
  origemCidade: "",
  origemUf: "",
};

const getOptionValue = (option) => {
  if (!option) {
    return null;
  }

  return option.value ?? option.id ?? option.Id ?? option.transportadora_id ?? null;
};

const getOptionLabel = (option) => {
  if (!option) {
    return "";
  }

  return (
    option.label ||
    option.transportadora_fantasia ||
    option.fantasia ||
    option.nome ||
    option.razaoSocial ||
    ""
  );
};

const normalizeSearch = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const normalizeDigits = (value) => String(value || "").replace(/\D/g, "");

function normalizeUpper(value) {
  return String(value || "").trim().toUpperCase();
}

function toDateInputValue(value) {
  const raw = String(value || "").trim();
  if (!raw) {
    return "";
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return raw;
  }

  const slashMatch = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (slashMatch) {
    const [, day, month, year] = slashMatch;
    return `${year}-${month}-${day}`;
  }

  return "";
}

function extractDetectedCityNames(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item || "").trim())
      .filter(Boolean);
  }

  return String(value || "")
    .split(/[,;|\n]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function normalizeCityRecord(city) {
  if (!city) {
    return null;
  }

  const id = city.id ?? city.Id ?? city.value ?? null;
  const nome = city.nome ?? city.Nome ?? city.label ?? "";
  const cepInicial =
    city.cepInicial ??
    city.cep_inicial ??
    city.cepInicialDestino ??
    city.CEPInicial ??
    city.CEP_InicialDestino ??
    null;
  const cepFinal =
    city.cepFinal ??
    city.cep_final ??
    city.cepFinalDestino ??
    city.CEPFinal ??
    city.CEP_FinalDestino ??
    null;

  return {
    id: id !== null && id !== undefined ? String(id) : "",
    nome,
    cepInicial,
    cepFinal,
    isCapital: Boolean(
      city.staCapitalEstado ??
      city.Sta_CapitalEstado ??
      city.sta_capital_estado ??
      city.capital ??
      false
    ),
    raw: city,
  };
}

function routeCostsMatch(routeA, routeB) {
  return CUSTO_KEYS.every((key) => {
    const left = String(routeA?.values?.[key] ?? "").trim();
    const right = String(routeB?.values?.[key] ?? "").trim();
    return left === right;
  });
}

function buildWeightRangeLines(values, columnsExpected, opts = {}) {
  return CUSTO_KEYS
    .filter((key) => values?.[key] !== "" && values?.[key] !== null && values?.[key] !== undefined)
    .map((key) => ({
      columnKey: key,
      columnLabel: getColumnLabel(columnsExpected, key, opts),
      ...getColumnWeightRange(columnsExpected, key, opts),
      custo: toNumericValue(values[key]),
    }));
}

function toNumericValue(value) {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  const parsed = typeof value === "number" ? value : parseFloat(String(value).replace(",", "."));
  return Number.isNaN(parsed) ? null : parsed;
}

function buildInitialColumnCalculationModes(columnsExpected) {
  return CUSTO_KEYS.reduce((acc, key) => {
    const column = (columnsExpected || []).find((item) => item.key === key);
    acc[key] = COLUMN_CALCULATION_MODES.includes(column?.calculationMode) ? column.calculationMode : "fixed";
    return acc;
  }, {});
}

function buildResolvedLineSignature(line, taxaValueOverrides = {}, excludedTaxaKeys = {}) {
  const costs = CUSTO_KEYS.map((key) => toNumericValue(line?.values?.[key]));
  const taxas = TAXA_KEYS.map((key) => (
    excludedTaxaKeys[key]
      ? null
      : taxaValueOverrides[key] !== undefined
        ? toNumericValue(taxaValueOverrides[key])
        : toNumericValue(line?.values?.[key])
  ));

  return JSON.stringify({ costs, taxas });
}

function buildPrazoImportRoutePayload(routes) {
  return (Array.isArray(routes) ? routes : []).map((route) => ({
    routeIndex: route.routeIndex,
    ufDestino: route.ufDestino || "",
    routeLabel: route.routeLabel || "",
    destinationType: inferDestinationType(route),
  }));
}

function createEmptyPrazoImportMapping() {
  return {
    sheetName: "",
    ufColumnIndex: "",
    cidadeColumnIndex: "",
    rotaColumnIndex: "",
    prazoColumnIndex: "",
  };
}

function buildPrazoImportMappingFromWorkbook(workbook) {
  return {
    sheetName: workbook?.selectedSheetName || "",
    ufColumnIndex: workbook?.effectiveMapping?.ufColumnIndex ?? "",
    cidadeColumnIndex: workbook?.effectiveMapping?.cidadeColumnIndex ?? "",
    rotaColumnIndex: workbook?.effectiveMapping?.rotaColumnIndex ?? "",
    prazoColumnIndex: workbook?.effectiveMapping?.prazoColumnIndex ?? "",
  };
}

function findMatchingCityRecordByImportName(cities, importedName) {
  const target = normalizeSearch(importedName);
  if (!target) {
    return null;
  }

  const list = Array.isArray(cities) ? cities : [];
  const exact = list.find((city) => normalizeSearch(city.nome) === target);
  if (exact) {
    return exact;
  }

  const contains = list.find((city) => {
    const cityName = normalizeSearch(city.nome);
    return cityName.includes(target) || target.includes(cityName);
  });

  return contains || null;
}

function buildPreviewCityKey(uf, cityId, cityName) {
  const normalizedUf = normalizeUpper(uf);
  const normalizedId = cityId !== null && cityId !== undefined && cityId !== ""
    ? String(cityId)
    : "";

  if (normalizedId) {
    return `${normalizedUf}|id:${normalizedId}`;
  }

  return `${normalizedUf}|name:${normalizeSearch(cityName)}`;
}

function getAdditionalChargeSourceMeta(source) {
  const normalized = String(source || "").toLowerCase();

  if (normalized.includes("parser") && normalized.includes("llm")) {
    return {
      label: "Parser + LLM",
      color: "#9b59b6",
      background: "rgba(155,89,182,0.12)",
    };
  }

  if (normalized.includes("llm")) {
    return {
      label: "LLM",
      color: "#f39c12",
      background: "rgba(243,156,18,0.12)",
    };
  }

  return {
    label: "Parser OCR",
    color: "#2ecc71",
    background: "rgba(46,204,113,0.12)",
  };
}

function tokenizeTaxaMatchText(...values) {
  return Array.from(new Set(
    values
      .join(" ")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .map((token) => token.trim())
      .filter((token) => token.length > 1 && !MATCH_TOKEN_STOPWORDS.has(token))
  ));
}

function findLinkedFixedTaxaKey(additionalCharge) {
  const search = normalizeSearch([
    additionalCharge?.label,
    additionalCharge?.description,
    additionalCharge?.sourceLine,
  ].filter(Boolean).join(" "));

  if (!search) {
    return null;
  }

  if (search.includes("advalorem") || (search.includes("frete") && search.includes("valor"))) {
    return "ad_valorem";
  }

  if (search.includes("gris")) {
    return "gris";
  }

  if (search.includes("seguro") || search.includes("obrigatorio") || search.includes("tso")) {
    return "seguro_obrigatorio";
  }

  if (search.includes("pedag")) {
    return "pedagio";
  }

  return null;
}

function buildAdditionalChargeMatchTokens(additionalCharge) {
  const tokens = new Set(
    tokenizeTaxaMatchText(
      additionalCharge?.label,
      additionalCharge?.description,
      additionalCharge?.sourceLine
    )
  );

  if (tokens.has("ctrc")) tokens.add("cte");
  if (tokens.has("pedagio")) tokens.add("pedag");
  if (tokens.has("sefaz")) tokens.add("tas");
  if (tokens.has("dificuldade") && tokens.has("entrega")) tokens.add("tde");
  if (tokens.has("dificuldade") && tokens.has("acesso")) tokens.add("tda");
  if (tokens.has("zona") && tokens.has("rural")) tokens.add("tzr");
  if (tokens.has("reentrega")) tokens.add("re");
  if (tokens.has("armazenagem")) tokens.add("armaz");
  if (tokens.has("gris")) tokens.add("gris");
  if (tokens.has("seguro")) tokens.add("obrigatorio");
  if (tokens.has("frete") && tokens.has("valor")) {
    tokens.add("ad");
    tokens.add("valorem");
    tokens.add("advalorem");
  }

  return Array.from(tokens);
}

function buildFretaTaxaMatchesForAdditionalCharge(additionalCharge, fretaTaxas) {
  const labelSearch = normalizeSearch(additionalCharge?.label);
  const sourceSearch = normalizeSearch(additionalCharge?.sourceLine || additionalCharge?.description);
  const matchTokens = buildAdditionalChargeMatchTokens(additionalCharge);

  return (Array.isArray(fretaTaxas) ? fretaTaxas : [])
    .map((fretaTaxa) => {
      const fretaTaxaSearch = normalizeSearch([
        fretaTaxa?.nome,
        fretaTaxa?.descricao,
        fretaTaxa?.codigo,
        fretaTaxa?.tipo,
        fretaTaxa?.por,
      ].filter(Boolean).join(" "));

      if (!fretaTaxaSearch) {
        return null;
      }

      let score = 0;

      if (labelSearch && fretaTaxaSearch.includes(labelSearch)) {
        score += 12;
      }

      if (sourceSearch && fretaTaxaSearch.includes(sourceSearch)) {
        score += 8;
      }

      matchTokens.forEach((token) => {
        if (fretaTaxaSearch.includes(token)) {
          score += token.length >= 4 ? 4 : 2;
        }
      });

      if (labelSearch.includes("ctrc") && (fretaTaxaSearch.includes("ctrc") || fretaTaxaSearch.includes("cte"))) {
        score += 8;
      }

      if (labelSearch.includes("sefaz") && fretaTaxaSearch.includes("sefaz")) {
        score += 8;
      }

      if (labelSearch.includes("pedag") && fretaTaxaSearch.includes("pedag")) {
        score += 8;
      }

      if (labelSearch.includes("gris") && fretaTaxaSearch.includes("gris")) {
        score += 8;
      }

      if (labelSearch.includes("armazen") && fretaTaxaSearch.includes("armazen")) {
        score += 8;
      }

      if (labelSearch.includes("reentrega") && fretaTaxaSearch.includes("reentrega")) {
        score += 8;
      }

      if (labelSearch.includes("dificuldade") && fretaTaxaSearch.includes("dificuldade")) {
        score += 6;
      }

      if (score <= 0) {
        return null;
      }

      return { fretaTaxa, score };
    })
    .filter(Boolean)
    .sort((left, right) => {
      if (right.score !== left.score) {
        return right.score - left.score;
      }

      return String(left.fretaTaxa?.nome || "").localeCompare(String(right.fretaTaxa?.nome || ""));
    })
    .map((item) => item.fretaTaxa)
    .slice(0, 5);
}

const buildReviewForm = (draft) => ({
  transportadoraNome: draft?.transportadora?.nome || "",
  transportadoraCnpj: draft?.transportadora?.cnpj || "",
  clienteNome: draft?.cliente?.nome || "",
  clienteCnpj: draft?.cliente?.cnpj || "",
  tituloTabela: draft?.tituloTabela || "",
  vigenciaInicio: draft?.vigencia?.inicio || "",
  vigenciaFim: draft?.vigencia?.fim || "",
  origemCidade: draft?.origem?.cidade || "",
  origemUf: draft?.origem?.uf || "",
});

const parseBackendError = (error) => {
  const payload = error?.response?.data;

  if (typeof payload === "string" && payload.trim()) {
    return payload;
  }

  if (payload && typeof payload === "object") {
    if (typeof payload.error === "string" && payload.error.trim()) {
      return payload.error;
    }

    if (typeof payload.message === "string" && payload.message.trim()) {
      return payload.message;
    }
  }

  return "Nao foi possivel analisar o PDF.";
};

const buildTransportadoraResolution = ({
  reviewForm,
  selectedTransportadora,
}) => {
  if (selectedTransportadora) {
    return {
      mode: "use_existing",
      label: "Usar transportadora existente",
      transportadora: {
        id: getOptionValue(selectedTransportadora),
        nome: getOptionLabel(selectedTransportadora),
      },
      suggestedCreate: null,
    };
  }

  if (reviewForm.transportadoraNome || reviewForm.transportadoraCnpj) {
    return {
      mode: "suggest_create",
      label: "Sugerir criacao de transportadora candidata",
      transportadora: null,
      suggestedCreate: {
        nome: reviewForm.transportadoraNome || null,
        cnpj: reviewForm.transportadoraCnpj || null,
      },
    };
  }

  return {
    mode: "unresolved",
    label: "Transportadora ainda nao resolvida",
    transportadora: null,
    suggestedCreate: null,
  };
};

const InfoBlock = ({ label, value, color = "#dbe7ff" }) => (
  <div style={{ marginBottom: "0.85rem" }}>
    <div style={fieldLabelStyle}>{label}</div>
    <div style={{ color, fontSize: "0.88rem", lineHeight: 1.5 }}>{value || "-"}</div>
  </div>
);

const Chip = ({ children, color = "#4c9dff", background = "rgba(76,157,255,0.12)" }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      padding: "0.28rem 0.55rem",
      borderRadius: 999,
      border: `1px solid ${color}`,
      color,
      background,
      fontSize: "0.76rem",
      marginRight: "0.4rem",
      marginBottom: "0.4rem",
    }}
  >
    {children}
  </span>
);

const MatchingBadge = ({ status, label }) => {
  const palette =
    status === "matched"
      ? { color: "#2ecc71", background: "rgba(46,204,113,0.12)" }
      : status === "missing"
      ? { color: "#f39c12", background: "rgba(243,156,18,0.12)" }
      : { color: "#8f9bc7", background: "rgba(143,155,199,0.12)" };

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "0.34rem 0.65rem",
        borderRadius: 999,
        border: `1px solid ${palette.color}`,
        color: palette.color,
        background: palette.background,
        fontSize: "0.74rem",
        fontWeight: 600,
      }}
    >
      {label}
    </span>
  );
};

/* eslint-disable react-hooks/rules-of-hooks */
function NovaTabelaFreteContent({ history, addToast, empresa }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [reviewForm, setReviewForm] = useState(emptyReviewForm);
  const [analysisError, setAnalysisError] = useState("");
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [transportadoras, setTransportadoras] = useState([]);
  const [loadingTransportadoras, setLoadingTransportadoras] = useState(false);
  const [selectedTransportadoraId, setSelectedTransportadoraId] = useState("");
  const [editableRoutes, setEditableRoutes] = useState([]);
  const [prazoExcelFile, setPrazoExcelFile] = useState(null);
  const [loadingPrazoExcel, setLoadingPrazoExcel] = useState(false);
  const [prazoImportPreview, setPrazoImportPreview] = useState(null);
  const [prazoImportMapping, setPrazoImportMapping] = useState(createEmptyPrazoImportMapping());
  const [prazoImportedAssignments, setPrazoImportedAssignments] = useState([]);
  const [prazoImportResult, setPrazoImportResult] = useState(null);

  // TASK-876: taxa confirmations (columnKey → fretaTaxaId selecionado) e value overrides
  const [taxaConfirmations, setTaxaConfirmations] = useState({});
  const [taxaValueOverrides, setTaxaValueOverrides] = useState({});
  const [taxaValorRegraOverrides, setTaxaValorRegraOverrides] = useState({});
  const [excludedTaxaKeys, setExcludedTaxaKeys] = useState({});
  const [additionalChargeConfirmations, setAdditionalChargeConfirmations] = useState({});
  const [additionalChargeValueOverrides, setAdditionalChargeValueOverrides] = useState({});
  const [additionalChargeValorRegraOverrides, setAdditionalChargeValorRegraOverrides] = useState({});
  const [excludedAdditionalChargeKeys, setExcludedAdditionalChargeKeys] = useState({});
  const [columnCalculationModes, setColumnCalculationModes] = useState({});
  const [columnDivisors, setColumnDivisors] = useState({});

  // TASK-874: feedback OCR
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [loadingSalvar, setLoadingSalvar] = useState(false);
  const isSavingRef = useRef(false);
  const saveRequestRef = useRef({ id: null, signature: null });
  const [loadingUltimoOcr, setLoadingUltimoOcr] = useState(false);
  const sessionRestoreRef = useRef(null);
  // PO1-2859: tabela salva nesta sessão (para o bloco de anexos) e controle do auto-anexo do PDF.
  const [savedTabelaId, setSavedTabelaId] = useState(null);
  const anexoEnviadoRef = useRef({ id: null });

  // Metadados usados no JSON estruturado final
  const [saveForm, setSaveForm] = useState({
    nome: "",
    idFreteUnidade: "",
    idModal: "",
    idNegocio: "",
    idTipoServico: "",
    idTipoFrete: "",
    dataValIni: "",
    dataValFim: "",
    origemCidade: "",
    origemUf: "",
  });
  const [freteUnidades, setFreteUnidades] = useState([]);
  const [modais, setModais] = useState([]);
  const [tiposFrete, setTiposFrete] = useState([]);
  const [negocios, setNegocios] = useState([]);
  const [estados, setEstados] = useState([]);
  const [cidadesByUf, setCidadesByUf] = useState({});
  const [loadingCidadesByUf, setLoadingCidadesByUf] = useState({});
  const [capitalSelectionsByUf, setCapitalSelectionsByUf] = useState({});
  const [capitalReviewTouchedByUf, setCapitalReviewTouchedByUf] = useState({});
  const [citySearchByUf, setCitySearchByUf] = useState({});

  // TASK-875: wizard de cadastro de transportadora
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardForm, setWizardForm] = useState({ cnpj: "", razaoSocial: "", fantasia: "", cep: "" });
  const [wizardSelectedTipoServico, setWizardSelectedTipoServico] = useState("");
  const [tiposServico, setTiposServico] = useState([]);
  const [loadingCnpj, setLoadingCnpj] = useState(false);
  const [loadingWizardSave, setLoadingWizardSave] = useState(false);

  const empresaId = getOptionValue(empresa);
  const embarcadorLabel = empresa?.label || empresa?.value || "-";
  const currentColumnsExpected = analysis?.structuredJson?.tableModel?.columnsExpected || [];
  // PO1-3916: unidade escolhida no salvar. Em PcFx (percentual sobre a NF) as
  // faixas são em R$ de NF, sem as faixas padrão em kg, e o modo é só Fixo.
  const saveUnidadeCodigo =
    freteUnidades.find((u) => String(u.id) === String(saveForm.idFreteUnidade))?.codigo || "";
  const isPcFx = saveUnidadeCodigo === UNIDADE_PCFX;
  const draftAdditionalCharges = Array.isArray(analysis?.draft?.additionalCharges)
    ? analysis.draft.additionalCharges
    : [];
  const detectedAdditionalCharges = draftAdditionalCharges.length > 0
    ? draftAdditionalCharges
    : (Array.isArray(analysis?.structuredJson?.supportData?.additionalCharges)
      ? analysis.structuredJson.supportData.additionalCharges
      : []);
  const semantic = analysis?.semanticAnalysis || analysis?.draft?.semanticAnalysis || null;
  const semanticAnalysisSummary = !analysis
    ? null
    : !semantic
      ? {
          label: "Sem LLM",
          detail: "Generalidades exibidas apenas do parser/OCR local.",
          color: "#8f9bc7",
          background: "rgba(143,155,199,0.12)",
        }
      : {
          label: `LLM ${semantic.confidence || "sem_conf"}`,
          detail: `Analise semantica disponivel via ${semantic.provider || semantic.model || "LLM"}.`,
          color: "#f39c12",
          background: "rgba(243,156,18,0.12)",
        };
  useEffect(() => {
    const session = sessionRestoreRef.current;
    sessionRestoreRef.current = null; // consume once

    if (!analysis?.structuredJson?.tableModel) {
      setEditableRoutes([]);
      setPrazoExcelFile(null);
      setPrazoImportPreview(null);
      setPrazoImportMapping(createEmptyPrazoImportMapping());
      setPrazoImportedAssignments([]);
      setPrazoImportResult(null);
    } else {
      const restoredRoutes = session && session.editableRoutes && session.editableRoutes.length > 0
        ? session.editableRoutes
        : buildEditableRoutes(analysis.structuredJson.tableModel);
      setEditableRoutes(restoredRoutes);
      setPrazoExcelFile(null);
      setPrazoImportPreview(session?.prazoImportPreview || null);
      setPrazoImportMapping(
        session?.prazoImportMapping
          || buildPrazoImportMappingFromWorkbook(session?.prazoImportPreview?.workbook)
      );
      setPrazoImportedAssignments(Array.isArray(session?.prazoImportedAssignments) ? session.prazoImportedAssignments : []);
      setPrazoImportResult(session?.prazoImportResult || null);
    }
    setCidadesByUf({});
    setLoadingCidadesByUf({});
    setCapitalSelectionsByUf(session ? (session.capitalSelectionsByUf || {}) : {});
    setCapitalReviewTouchedByUf({});
    setCitySearchByUf({});
    setColumnCalculationModes(
      session && session.columnCalculationModes
        ? session.columnCalculationModes
        : buildInitialColumnCalculationModes(analysis?.structuredJson?.tableModel?.columnsExpected || [])
    );
    setColumnDivisors(session && session.columnDivisors ? session.columnDivisors : {});

    if (session) {
      setSaveForm(session.saveForm || { nome: "", idFreteUnidade: "", idModal: "", idNegocio: "", idTipoServico: "", idTipoFrete: "", dataValIni: "", dataValFim: "", origemCidade: "", origemUf: "" });
      setSelectedTransportadoraId(session.selectedTransportadoraId || "");
      setTaxaConfirmations(session.taxaConfirmations || {});
      setTaxaValueOverrides(session.taxaValueOverrides || {});
      setTaxaValorRegraOverrides(session.taxaValorRegraOverrides || {});
      setExcludedTaxaKeys(session.excludedTaxaKeys || {});
      setAdditionalChargeConfirmations(session.additionalChargeConfirmations || {});
      setAdditionalChargeValueOverrides(session.additionalChargeValueOverrides || {});
      setAdditionalChargeValorRegraOverrides(session.additionalChargeValorRegraOverrides || {});
      setExcludedAdditionalChargeKeys(session.excludedAdditionalChargeKeys || {});
    } else {
      setTaxaConfirmations({});
      setTaxaValueOverrides({});
      setTaxaValorRegraOverrides({});
      setExcludedTaxaKeys({});
      setAdditionalChargeConfirmations({});
      setAdditionalChargeValueOverrides({});
      setAdditionalChargeValorRegraOverrides({});
      setExcludedAdditionalChargeKeys({});
      setPrazoImportPreview(null);
      setPrazoImportMapping(createEmptyPrazoImportMapping());
      setPrazoImportedAssignments([]);
      setPrazoImportResult(null);
    }
  }, [analysis]);

  useEffect(() => {
    if (!analysis?.draft) {
      setReviewForm(emptyReviewForm);
      setSelectedTransportadoraId("");
      return;
    }

    setReviewForm(buildReviewForm(analysis.draft));
  }, [analysis]);

  useEffect(() => {
    setSaveForm((current) => ({
      ...current,
      nome: reviewForm.tituloTabela || "",
      dataValIni: toDateInputValue(reviewForm.vigenciaInicio),
      dataValFim: toDateInputValue(reviewForm.vigenciaFim),
      origemCidade: reviewForm.origemCidade || "",
      origemUf: reviewForm.origemUf || "",
    }));
  }, [
    reviewForm.origemCidade,
    reviewForm.origemUf,
    reviewForm.tituloTabela,
    reviewForm.vigenciaFim,
    reviewForm.vigenciaInicio,
  ]);

  const detectedColumnCalculationMetadata = useMemo(() => {
    return CUSTO_KEYS.reduce((acc, key) => {
      const column = currentColumnsExpected.find((item) => item.key === key) || {};
      acc[key] = {
        suggestedMode: column.calculationMode === "excess_kg" ? "excess_kg" : "fixed",
        detectedByOcr: Boolean(column.calculationModeDetectedByOcr),
      };
      return acc;
    }, {});
  }, [currentColumnsExpected]);

  const loadTransportadoras = useCallback(async () => {
    if (!empresaId) {
      setTransportadoras([]);
      return;
    }
    try {
      setLoadingTransportadoras(true);
      const response = await api.get(`/transportadoras/${empresaId}`);
      setTransportadoras(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setTransportadoras([]);
    } finally {
      setLoadingTransportadoras(false);
    }
  }, [empresaId]);

  useEffect(() => {
    loadTransportadoras();
    return () => {};
  }, [loadTransportadoras]);

  // TASK-873: carregar dropdowns para salvar tabela
  useEffect(() => {
    api.get("/frete/unidades").then(r => setFreteUnidades(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get("/frete/modais").then(r => setModais(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get("/frete/tipos-servico").then(r => setTiposServico(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get("/frete/tipos-frete").then(r => setTiposFrete(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/frete/estados").then((r) => setEstados(Array.isArray(r.data) ? r.data : [])).catch(() => setEstados([]));
  }, []);

  useEffect(() => {
    if (!empresaId) { setNegocios([]); return; }
    api.get(`/embarcadores/${empresaId}/negocios`).then(r => setNegocios(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [empresaId]);

  const estadoIdByUf = useMemo(() => {
    return estados.reduce((acc, item) => {
      const uf = normalizeUpper(item?.uf || item?.sigla || item?.label);
      const id = item?.id ?? item?.Id ?? item?.value;
      if (uf && id !== null && id !== undefined) {
        acc[uf] = String(id);
      }
      return acc;
    }, {});
  }, [estados]);

  const detectedCapitalCitiesByUf = useMemo(() => {
    return (Array.isArray(analysis?.draft?.coberturasCapital) ? analysis.draft.coberturasCapital : []).reduce((acc, item) => {
      const uf = normalizeUpper(item?.uf);
      if (!uf) {
        return acc;
      }

      const names = extractDetectedCityNames(item?.cidades);
      if (names.length > 0) {
        acc[uf] = Array.from(new Set([...(acc[uf] || []), ...names]));
      }

      return acc;
    }, {});
  }, [analysis]);

  const capitalReviewUfs = useMemo(() => {
    const routeCountByUf = {};
    const capitalUfs = new Set(Object.keys(detectedCapitalCitiesByUf));

    editableRoutes.forEach((route) => {
      const uf = normalizeUpper(route.ufDestino);
      if (!VALID_UFS.has(uf)) {
        return;
      }

      routeCountByUf[uf] = (routeCountByUf[uf] || 0) + 1;

      if (inferDestinationType(route) === "capital" || normalizeSearch(route.routeLabel).includes("interior")) {
        capitalUfs.add(uf);
      }
    });

    Object.keys(routeCountByUf).forEach((uf) => {
      if (routeCountByUf[uf] > 1) {
        capitalUfs.add(uf);
      }
    });

    return Array.from(capitalUfs).sort();
  }, [detectedCapitalCitiesByUf, editableRoutes]);

  useEffect(() => {
    capitalReviewUfs.forEach((uf) => {
      const estadoId = estadoIdByUf[uf];
      if (!estadoId || cidadesByUf[uf] || loadingCidadesByUf[uf]) {
        return;
      }

      setLoadingCidadesByUf((prev) => ({ ...prev, [uf]: true }));

      api.get(`/frete/estados/${estadoId}/cidades`)
        .then((res) => {
          const normalized = (Array.isArray(res.data) ? res.data : [])
            .map(normalizeCityRecord)
            .filter(Boolean);
          setCidadesByUf((prev) => ({ ...prev, [uf]: normalized }));
        })
        .catch(() => {
          setCidadesByUf((prev) => ({ ...prev, [uf]: [] }));
        })
        .finally(() => {
          setLoadingCidadesByUf((prev) => ({ ...prev, [uf]: false }));
        });
    });
  }, [capitalReviewUfs, cidadesByUf, estadoIdByUf, loadingCidadesByUf]);

  useEffect(() => {
    capitalReviewUfs.forEach((uf) => {
      if (capitalReviewTouchedByUf[uf]) {
        return;
      }

      const cities = cidadesByUf[uf];
      if (!Array.isArray(cities) || cities.length === 0) {
        return;
      }

      setCapitalSelectionsByUf((prev) => {
        if (Array.isArray(prev[uf])) {
          return prev;
        }

        const detectedNames = detectedCapitalCitiesByUf[uf] || [];
        const detectedIds = cities
          .filter((city) =>
            detectedNames.some((name) => {
              const normalizedCity = normalizeSearch(city.nome);
              const normalizedDetected = normalizeSearch(name);
              return normalizedCity.includes(normalizedDetected) || normalizedDetected.includes(normalizedCity);
            })
          )
          .map((city) => city.id);

        const fallbackIds = cities.filter((city) => city.isCapital).map((city) => city.id);
        const nextIds = detectedIds.length > 0 ? detectedIds : fallbackIds;

        return {
          ...prev,
          [uf]: Array.from(new Set(nextIds)),
        };
      });
    });
  }, [capitalReviewTouchedByUf, capitalReviewUfs, cidadesByUf, detectedCapitalCitiesByUf]);

  const groupingSuggestions = useMemo(() => {
    return capitalReviewUfs.reduce((acc, uf) => {
      const interiorRoutes = editableRoutes.filter(
        (route) => normalizeUpper(route.ufDestino) === uf && inferDestinationType(route) === "interior"
      );

      if (interiorRoutes.length < 2) {
        return acc;
      }

      const [baseRoute, ...otherRoutes] = interiorRoutes;
      const canGroup = otherRoutes.every((route) => routeCostsMatch(baseRoute, route));

      acc.push({
        uf,
        canGroup,
        comparedColumns: CUSTO_KEYS,
        sourceRoutes: interiorRoutes.map((route) => route.routeLabel || getInteriorTier(route)),
        status: canGroup ? "candidate" : "distinct_values",
      });

      return acc;
    }, []);
  }, [capitalReviewUfs, editableRoutes]);

  const capitalReviewData = useMemo(() => {
    return capitalReviewUfs.map((uf) => {
      const cidades = Array.isArray(cidadesByUf[uf]) ? cidadesByUf[uf] : [];
      const selectedIds = new Set((capitalSelectionsByUf[uf] || []).map(String));
      const detectedNames = detectedCapitalCitiesByUf[uf] || [];
      const selectedCapitalCities = cidades.filter((city) => selectedIds.has(String(city.id)));
      const interiorCities = cidades.filter((city) => !selectedIds.has(String(city.id)));
      const routes = editableRoutes.filter((route) => normalizeUpper(route.ufDestino) === uf);
      const capitalRoute = routes.find((route) => inferDestinationType(route) === "capital") || null;
      const interiorRoutes = routes.filter((route) => inferDestinationType(route) === "interior");
      const groupingSuggestion = groupingSuggestions.find((item) => item.uf === uf) || null;

      return {
        uf,
        stateId: estadoIdByUf[uf] || null,
        loadingCities: Boolean(loadingCidadesByUf[uf]),
        detectedNames,
        selectedCapitalCityIds: Array.from(selectedIds),
        selectedCapitalCities: selectedCapitalCities.map((city) => ({
          id: city.id,
          nome: city.nome,
          cepInicial: city.cepInicial || null,
          cepFinal: city.cepFinal || null,
          isCapitalFallback: city.isCapital,
        })),
        interiorCities: interiorCities.map((city) => ({
          id: city.id,
          nome: city.nome,
          cepInicial: city.cepInicial || null,
          cepFinal: city.cepFinal || null,
          isCapitalFallback: city.isCapital,
        })),
        cityCount: cidades.length,
        capitalRoute: capitalRoute
          ? {
              routeIndex: capitalRoute.routeIndex,
              routeLabel: capitalRoute.routeLabel,
              values: capitalRoute.values,
            }
          : null,
        interiorRoutes: interiorRoutes.map((route) => ({
          routeIndex: route.routeIndex,
          routeLabel: route.routeLabel,
          tier: getInteriorTier(route),
          values: route.values,
        })),
        groupingSuggestion,
      };
    });
  }, [
    capitalReviewUfs,
    cidadesByUf,
    capitalSelectionsByUf,
    detectedCapitalCitiesByUf,
    editableRoutes,
    estadoIdByUf,
    groupingSuggestions,
    loadingCidadesByUf,
  ]);

  const prazoImportedCityAssignments = useMemo(() => {
    const resolvedLines = [];
    const assignedCityKeys = new Set();
    const unresolvedAssignments = [];
    const seenAssignments = new Set();

    (Array.isArray(prazoImportedAssignments) ? prazoImportedAssignments : []).forEach((assignment) => {
      const uf = normalizeUpper(assignment?.ufDestino);
      if (!uf) {
        return;
      }

      const route = editableRoutes.find((item) =>
        normalizeUpper(item.ufDestino) === uf &&
        String(item.routeIndex) === String(assignment?.matchedRouteIndex)
      );

      if (!route) {
        unresolvedAssignments.push({
          ...assignment,
          reason: "route_missing",
        });
        return;
      }

      const cities = cidadesByUf[uf];
      if (!Array.isArray(cities) || cities.length === 0) {
        return;
      }

      const matchedCity = findMatchingCityRecordByImportName(cities, assignment?.cidadeDestino);
      if (!matchedCity) {
        unresolvedAssignments.push({
          ...assignment,
          reason: "city_not_found",
        });
        return;
      }

      const cityKey = buildPreviewCityKey(uf, matchedCity.id, matchedCity.nome);
      const assignmentKey = `${cityKey}|route:${route.routeIndex}`;

      if (seenAssignments.has(assignmentKey)) {
        return;
      }

      seenAssignments.add(assignmentKey);
      assignedCityKeys.add(cityKey);

      resolvedLines.push({
        ufDestino: uf,
        tipoCobertura: inferDestinationType(route),
        routeLabel: route.routeLabel || assignment?.matchedRouteLabel || assignment?.routeRaw || "ROTA IMPORTADA",
        routeIndex: route.routeIndex,
        idCidadeDestino: matchedCity.id,
        cidadeDestino: matchedCity.nome,
        cepInicialDestino: matchedCity.cepInicial || null,
        cepFinalDestino: matchedCity.cepFinal || null,
        origemSelecao: "prazo_excel",
        values: {
          ...route.values,
          prazo_entrega: route.values?.prazo_entrega || (assignment?.prazo !== null && assignment?.prazo !== undefined ? String(assignment.prazo) : ""),
        },
        faixasPeso: buildWeightRangeLines(route.values, currentColumnsExpected, { pcFx: isPcFx }),
      });
    });

    return {
      resolvedLines,
      assignedCityKeys,
      unresolvedAssignments,
    };
  }, [cidadesByUf, currentColumnsExpected, editableRoutes, isPcFx, prazoImportedAssignments]);

  const freteTabelaCustoPreview = useMemo(() => {
    const preview = capitalReviewData.reduce((acc, item) => {
      const importedLines = prazoImportedCityAssignments.resolvedLines.filter((line) => line.ufDestino === item.uf);
      const hasImportedCoverageForUf = importedLines.length > 0;
      importedLines.forEach((line) => {
        acc.resolvedLines.push(line);
      });

      if (item.loadingCities) {
        acc.pendingGroups.push({
          uf: item.uf,
          reason: "city_catalog_loading",
          message: "Catalogo de cidades ainda carregando para esta UF.",
        });
        return acc;
      }

      if (item.cityCount === 0) {
        acc.pendingGroups.push({
          uf: item.uf,
          reason: "city_catalog_missing",
          message: "Nao foi possivel carregar cidades da UF para expandir a previa.",
        });
        return acc;
      }

      const remainingCapitalCities = item.selectedCapitalCities.filter((city) => (
        !prazoImportedCityAssignments.assignedCityKeys.has(buildPreviewCityKey(item.uf, city.id, city.nome))
      ));
      const remainingInteriorCities = item.interiorCities.filter((city) => (
        !prazoImportedCityAssignments.assignedCityKeys.has(buildPreviewCityKey(item.uf, city.id, city.nome))
      ));

      if (item.capitalRoute) {
        remainingCapitalCities.forEach((city) => {
          acc.resolvedLines.push({
            ufDestino: item.uf,
            tipoCobertura: "capital",
            routeLabel: item.capitalRoute.routeLabel || "CAPITAL",
            routeIndex: item.capitalRoute.routeIndex,
            idCidadeDestino: city.id,
            cidadeDestino: city.nome,
            cepInicialDestino: city.cepInicial,
            cepFinalDestino: city.cepFinal,
            origemSelecao: item.detectedNames.length > 0 ? "ocr_or_review" : "capital_fallback",
            values: { ...(item.capitalRoute.values || {}) },
            faixasPeso: buildWeightRangeLines(item.capitalRoute.values, currentColumnsExpected, { pcFx: isPcFx }),
          });
        });
      } else if (remainingCapitalCities.length > 0) {
        acc.pendingGroups.push({
          uf: item.uf,
          reason: "capital_route_missing",
          message: "Existem cidades marcadas como capital, mas nenhuma rota CAPITAL foi identificada para a UF.",
          candidateCities: remainingCapitalCities,
        });
      }

      if (remainingInteriorCities.length === 0) {
        return acc;
      }

      if (item.interiorRoutes.length === 1 || item.groupingSuggestion?.canGroup) {
        const sourceRoute = item.interiorRoutes[0];
        remainingInteriorCities.forEach((city) => {
          acc.resolvedLines.push({
            ufDestino: item.uf,
            tipoCobertura: "interior",
            routeLabel: item.groupingSuggestion?.canGroup
              ? `INTERIOR AGRUPADO (${item.groupingSuggestion.sourceRoutes.join(" + ")})`
              : sourceRoute?.routeLabel || "INTERIOR",
            routeIndex: sourceRoute?.routeIndex || null,
            idCidadeDestino: city.id,
            cidadeDestino: city.nome,
            cepInicialDestino: city.cepInicial,
            cepFinalDestino: city.cepFinal,
            origemSelecao: "restante_da_uf",
            values: { ...(sourceRoute?.values || {}) },
            faixasPeso: buildWeightRangeLines(sourceRoute?.values, currentColumnsExpected, { pcFx: isPcFx }),
          });
        });
      } else if (item.interiorRoutes.length > 1) {
        if (hasImportedCoverageForUf) {
          return acc;
        }

        acc.pendingGroups.push({
          uf: item.uf,
          reason: "distinct_interior_routes",
          message: "Existem multiplas rotas de interior com valores diferentes. O JSON sinaliza a UF, mas nao distribui cidades automaticamente.",
          candidateCities: remainingInteriorCities,
          routeVariants: item.interiorRoutes,
        });
      } else if (remainingInteriorCities.length > 0) {
        acc.pendingGroups.push({
          uf: item.uf,
          reason: "interior_route_missing",
          message: "Existem cidades de interior na UF, mas nenhuma rota de interior foi identificada.",
          candidateCities: remainingInteriorCities,
        });
      }

      return acc;
    }, {
      resolvedLines: [],
      pendingGroups: [],
      summary: {
        resolvedLineCount: 0,
        pendingGroupCount: 0,
      },
    });

    return {
      ...preview,
      summary: {
        resolvedLineCount: preview.resolvedLines.length,
        pendingGroupCount: preview.pendingGroups.length,
      },
    };
  }, [capitalReviewData, currentColumnsExpected, isPcFx, prazoImportedCityAssignments]);

  const handleCapitalSelectionChange = useCallback((uf, cityId, checked) => {
    setCapitalReviewTouchedByUf((prev) => ({ ...prev, [uf]: true }));
    setCapitalSelectionsByUf((prev) => {
      const current = new Set((prev[uf] || []).map(String));
      if (checked) {
        current.add(String(cityId));
      } else {
        current.delete(String(cityId));
      }

      return {
        ...prev,
        [uf]: Array.from(current),
      };
    });
  }, []);

  const handleApplyCapitalFallback = useCallback((uf) => {
    const cities = cidadesByUf[uf] || [];
    const detectedNames = detectedCapitalCitiesByUf[uf] || [];
    const fallbackIds = cities
      .filter((city) =>
        city.isCapital ||
        detectedNames.some((name) => {
          const normalizedCity = normalizeSearch(city.nome);
          const normalizedDetected = normalizeSearch(name);
          return normalizedCity.includes(normalizedDetected) || normalizedDetected.includes(normalizedCity);
        })
      )
      .map((city) => city.id);

    setCapitalReviewTouchedByUf((prev) => ({ ...prev, [uf]: true }));
    setCapitalSelectionsByUf((prev) => ({
      ...prev,
      [uf]: Array.from(new Set(fallbackIds)),
    }));
  }, [cidadesByUf, detectedCapitalCitiesByUf]);

  const handleClearCapitalSelection = useCallback((uf) => {
    setCapitalReviewTouchedByUf((prev) => ({ ...prev, [uf]: true }));
    setCapitalSelectionsByUf((prev) => ({
      ...prev,
      [uf]: [],
    }));
  }, []);

  const handleCitySearchChange = useCallback((uf, value) => {
    setCitySearchByUf((prev) => ({
      ...prev,
      [uf]: value,
    }));
  }, []);

  const transportadoraOptions = useMemo(() => {
    const fromContext = Array.isArray(empresa?.transportadoras) ? empresa.transportadoras : [];
    const merged = [...fromContext, ...transportadoras];
    const seen = new Set();

    return merged.reduce((acc, option) => {
      const label = getOptionLabel(option);
      const value = getOptionValue(option);
      const key = value !== null && value !== undefined ? `id:${value}` : `name:${normalizeSearch(label)}`;

      if (!label || !key || seen.has(key)) {
        return acc;
      }

      seen.add(key);
      acc.push(option);
      return acc;
    }, []);
  }, [empresa, transportadoras]);

  const matchedTransportadora = useMemo(() => {
    const targetName = normalizeSearch(reviewForm.transportadoraNome);
    const targetCnpj = normalizeDigits(reviewForm.transportadoraCnpj);

    if (!targetName && !targetCnpj) {
      return null;
    }

    return (
      transportadoraOptions.find((option) => {
        const optionName = normalizeSearch(getOptionLabel(option));
        const optionCnpj = normalizeDigits(
          option?.cnpj || option?.cnpjCpf || option?.cnpj_cpf || option?.documento
        );

        if (targetCnpj && optionCnpj && targetCnpj === optionCnpj) {
          return true;
        }

        if (!targetName || !optionName) {
          return false;
        }

        return optionName.includes(targetName) || targetName.includes(optionName);
      }) || null
    );
  }, [reviewForm.transportadoraCnpj, reviewForm.transportadoraNome, transportadoraOptions]);

  useEffect(() => {
    if (matchedTransportadora) {
      const nextValue = getOptionValue(matchedTransportadora);
      setSelectedTransportadoraId(
        nextValue !== null && nextValue !== undefined ? String(nextValue) : ""
      );
      return;
    }

    setSelectedTransportadoraId("");
  }, [matchedTransportadora]);

  const matchingStatus = matchedTransportadora
    ? {
        status: "matched",
        label: "Transportadora encontrada no embarcador",
      }
    : reviewForm.transportadoraNome
    ? {
        status: "missing",
        label: "Transportadora nao encontrada no cadastro atual",
      }
    : {
        status: "idle",
        label: "Transportadora ainda nao identificada",
      };

  const selectedTransportadora = useMemo(
    () =>
      transportadoraOptions.find((option) => {
        const optionValue = getOptionValue(option);
        return optionValue !== null && optionValue !== undefined
          ? String(optionValue) === String(selectedTransportadoraId)
          : false;
      }) || null,
    [selectedTransportadoraId, transportadoraOptions]
  );

  const transportadoraResolution = useMemo(
    () =>
      buildTransportadoraResolution({
        reviewForm,
        selectedTransportadora,
      }),
    [reviewForm, selectedTransportadora]
  );

  const structuredJsonPreview = useMemo(() => {
    if (!analysis?.structuredJson) {
      return null;
    }

    const originalCols = analysis.structuredJson.tableModel?.columnsExpected || [];
    const editedRoutes = editableRoutes.length > 0
      ? editableRoutes.map((er) => {
          const orig = (analysis.structuredJson.tableModel?.routes || []).find(
            (r) => r.routeIndex === er.routeIndex
          ) || {};
          const columnMap = {};
          originalCols.forEach((col) => {
            const v = er.values[col.key];
            columnMap[col.key] = v !== "" && v !== undefined ? parseFloat(v) : null;
          });
          return {
            ...orig,
            ufDestino: er.ufDestino,
            routeLabel: er.routeLabel,
            destinationType: inferDestinationType(er),
            columnMap,
            flowLines: undefined,
          };
        })
      : analysis.structuredJson.tableModel?.routes;

    return {
      ...analysis.structuredJson,
      header: {
        ...analysis.structuredJson.header,
        transportadora: {
          nome: reviewForm.transportadoraNome || null,
          cnpj: reviewForm.transportadoraCnpj || null,
        },
        cliente: {
          nome: reviewForm.clienteNome || null,
          cnpj: reviewForm.clienteCnpj || null,
        },
        tituloTabela: reviewForm.tituloTabela || null,
        origem: {
          cidade: reviewForm.origemCidade || null,
          uf: reviewForm.origemUf || null,
        },
        vigencia: {
          ...analysis.structuredJson.header?.vigencia,
          inicio: reviewForm.vigenciaInicio || null,
          fim: reviewForm.vigenciaFim || null,
        },
      },
      tableModel: {
        ...analysis.structuredJson.tableModel,
        columnsExpected: originalCols.map((col) => ({
          ...col,
          calculationMode: CUSTO_KEYS.includes(col.key)
            ? (columnCalculationModes[col.key] || col.calculationMode || "fixed")
            : (col.calculationMode || null),
        })),
        routes: editedRoutes,
        flowLines: undefined,
      },
      tableDraft: {
        nome: saveForm.nome || null,
        idFreteUnidade: saveForm.idFreteUnidade || null,
        idModal: saveForm.idModal || null,
        idNegocio: saveForm.idNegocio || null,
        dataValIni: saveForm.dataValIni || null,
        dataValFim: saveForm.dataValFim || null,
        origemCidade: saveForm.origemCidade || null,
        origemUf: saveForm.origemUf || null,
      },
      capitalReview: capitalReviewData,
      groupingSuggestions,
      freteTabelaCustoPreview,
      review: {
        status: "draft_json_ready",
        transportadoraResolution,
        matchingStatus,
        capitalReviewSummary: {
          reviewedUfCount: capitalReviewData.length,
          resolvedPreviewLines: freteTabelaCustoPreview.summary.resolvedLineCount,
          pendingPreviewGroups: freteTabelaCustoPreview.summary.pendingGroupCount,
        },
      },
    };
  }, [
    analysis,
    capitalReviewData,
    columnCalculationModes,
    editableRoutes,
    freteTabelaCustoPreview,
    groupingSuggestions,
    matchingStatus,
    reviewForm,
    saveForm,
    transportadoraResolution,
  ]);

  const structuredJsonText = useMemo(
    () => (structuredJsonPreview ? JSON.stringify(structuredJsonPreview, null, 2) : ""),
    [structuredJsonPreview]
  );

  const effectiveTaxaConfigs = useMemo(() => {
    return TAXA_KEYS.map((key) => {
      const info = analysis?.taxaAnalysis?.[key] || {};
      const allTaxas = analysis?.allFretaTaxas || [];
      const confirmedId = taxaConfirmations[key];
      const suggestedId = info.fretaTaxaMatches && info.fretaTaxaMatches.length > 0
        ? info.fretaTaxaMatches[0].id
        : null;
      const idFretaTaxa = confirmedId !== undefined && confirmedId !== null && confirmedId !== ""
        ? parseInt(confirmedId, 10)
        : suggestedId !== null && suggestedId !== undefined
          ? parseInt(suggestedId, 10)
          : null;
      const selectedFretaTaxa = allTaxas.find((item) => String(item.id) === String(idFretaTaxa)) || null;
      const suggestedValorRegra = idFretaTaxa
        ? inferSuggestedValorRegra({ columnKey: key, freteTaxa: selectedFretaTaxa })
        : null;
      const explicitValorRegra = taxaValorRegraOverrides[key] !== undefined
        ? toNumericValue(taxaValorRegraOverrides[key])
        : null;

      return {
        columnKey: key,
        idFretaTaxa: Number.isNaN(idFretaTaxa) ? null : idFretaTaxa,
        valorRegra: explicitValorRegra !== null ? explicitValorRegra : suggestedValorRegra,
        valorRegraManual: explicitValorRegra,
        valorRegraSugerido: suggestedValorRegra,
        valorGlobal: taxaValueOverrides[key] !== undefined
          ? toNumericValue(taxaValueOverrides[key])
          : toNumericValue(info.valorUnico),
        possuiValorDetectado: (info.rotasComValor || 0) > 0,
        possuiValorConfigurado:
          (info.rotasComValor || 0) > 0 ||
          (taxaValueOverrides[key] !== undefined && toNumericValue(taxaValueOverrides[key]) !== null),
        excluded: Boolean(excludedTaxaKeys[key]),
      };
    });
  }, [analysis, excludedTaxaKeys, taxaConfirmations, taxaValueOverrides, taxaValorRegraOverrides]);

  const effectiveAdditionalChargeConfigs = useMemo(() => {
    const allTaxas = analysis?.allFretaTaxas || [];

    return detectedAdditionalCharges.map((charge, index) => {
      const chargeKey = String(charge?.key || `additional_charge_${index + 1}`);
      const matches = buildFretaTaxaMatchesForAdditionalCharge(charge, allTaxas);
      const confirmedId = additionalChargeConfirmations[chargeKey];
      const suggestedId = matches.length > 0 ? matches[0].id : null;
      const idFretaTaxa = confirmedId !== undefined && confirmedId !== null && confirmedId !== ""
        ? parseInt(confirmedId, 10)
        : suggestedId !== null && suggestedId !== undefined
          ? parseInt(suggestedId, 10)
          : null;
      const selectedFretaTaxa = allTaxas.find((item) => String(item.id) === String(idFretaTaxa)) || null;
      const linkedFixedKey = findLinkedFixedTaxaKey(charge);
      const explicitValorRegra = additionalChargeValorRegraOverrides[chargeKey] !== undefined
        ? toNumericValue(additionalChargeValorRegraOverrides[chargeKey])
        : null;
      const rawDetectedValue =
        charge?.parsedValue !== undefined && charge?.parsedValue !== null
          ? charge.parsedValue
          : charge?.rawValue;
      const overriddenValue = additionalChargeValueOverrides[chargeKey];
      const valorGlobal = overriddenValue !== undefined
        ? toNumericValue(overriddenValue)
        : toNumericValue(rawDetectedValue);
      const routeInfo = linkedFixedKey ? (analysis?.taxaAnalysis?.[linkedFixedKey] || {}) : {};
      const shouldHideLinkedRouteCard = Boolean(
        linkedFixedKey &&
        !routeInfo.isUniforme &&
        (routeInfo.rotasComValor || 0) === 0
      );

      const suggestedValorRegra = idFretaTaxa
        ? inferSuggestedValorRegra({
            columnKey: linkedFixedKey || chargeKey,
            freteTaxa: selectedFretaTaxa,
          })
        : null;

      return {
        itemKey: chargeKey,
        label: charge?.label || chargeKey,
        description: charge?.description || null,
        rawValue: charge?.rawValue || null,
        parsedValue: charge?.parsedValue ?? null,
        sourceLine: charge?.sourceLine || null,
        valueType: charge?.valueType || null,
        interpolationGroup: charge?.interpolationGroup || null,
        source: charge?.source || "parser",
        linkedFixedKey,
        shouldHideLinkedRouteCard,
        matches,
        idFretaTaxa: Number.isNaN(idFretaTaxa) ? null : idFretaTaxa,
        valorRegra: explicitValorRegra !== null ? explicitValorRegra : suggestedValorRegra,
        valorRegraManual: explicitValorRegra,
        valorRegraSugerido: suggestedValorRegra,
        valorGlobal,
        possuiValorDetectado: valorGlobal !== null,
        excluded: Boolean(excludedAdditionalChargeKeys[chargeKey]),
      };
    });
  }, [
    additionalChargeConfirmations,
    additionalChargeValorRegraOverrides,
    additionalChargeValueOverrides,
    analysis,
    detectedAdditionalCharges,
    excludedAdditionalChargeKeys,
  ]);

  const hiddenRouteTaxaKeys = useMemo(() => {
    return new Set(
      effectiveAdditionalChargeConfigs
        .filter((config) => config.shouldHideLinkedRouteCard)
        .map((config) => config.linkedFixedKey)
        .filter(Boolean)
    );
  }, [effectiveAdditionalChargeConfigs]);

  const unifiedTaxaEntries = useMemo(() => {
    const entries = [];

    TAXA_KEYS.forEach((key) => {
      if (!hiddenRouteTaxaKeys.has(key)) {
        entries.push({
          entryType: "route",
          itemKey: key,
          label: COL_SHORT[key] || key,
          routeInfo: analysis?.taxaAnalysis?.[key] || {},
        });
      }
    });

    effectiveAdditionalChargeConfigs.forEach((config) => {
      entries.push({
        entryType: "additional",
        itemKey: config.itemKey,
        label: config.label,
        config,
      });
    });

    return entries;
  }, [analysis, effectiveAdditionalChargeConfigs, hiddenRouteTaxaKeys]);

  const resolvedLinePreparation = useMemo(() => {
    const rows = [];
    const conflicts = [];
    const byCity = new Map();
    let dedupedCount = 0;

    (freteTabelaCustoPreview.resolvedLines || []).forEach((line) => {
      const cityKey = line?.idCidadeDestino
        ? `${normalizeUpper(line.ufDestino)}|id:${line.idCidadeDestino}`
        : `${normalizeUpper(line.ufDestino)}|name:${normalizeSearch(line?.cidadeDestino)}`;
      const signature = buildResolvedLineSignature(line, taxaValueOverrides, excludedTaxaKeys);
      const existing = byCity.get(cityKey);

      if (!existing) {
        byCity.set(cityKey, { line, signature });
        rows.push(line);
        return;
      }

      if (existing.signature === signature) {
        dedupedCount += 1;
        return;
      }

      conflicts.push({
        ufDestino: line.ufDestino,
        cidadeDestino: line.cidadeDestino || existing.line.cidadeDestino || "Cidade sem nome",
        idCidadeDestino: line.idCidadeDestino || existing.line.idCidadeDestino || null,
        routeAtual: line.routeLabel || "ROTA ATUAL",
        routeAnterior: existing.line.routeLabel || "ROTA ANTERIOR",
      });
    });

    return { rows, conflicts, dedupedCount };
  }, [excludedTaxaKeys, freteTabelaCustoPreview.resolvedLines, taxaValueOverrides]);

  const handleCopyJson = useCallback(async () => {
    if (!structuredJsonText) {
      return;
    }

    try {
      await navigator.clipboard.writeText(structuredJsonText);
      addToast({
        place: "tr",
        type: "success",
        title: "JSON copiado",
        description: "O JSON estruturado foi copiado para a area de transferencia.",
        icon: "tim-icons icon-check-2",
      });
    } catch (error) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Falha ao copiar",
        description: "Nao foi possivel copiar o JSON estruturado.",
        icon: "tim-icons icon-alert-circle-exc",
      });
    }
  }, [addToast, structuredJsonText]);

  const handleFieldChange = useCallback((field, value) => {
    setReviewForm((current) => ({
      ...current,
      [field]: value,
    }));
  }, []);

  const handleAnalyze = useCallback(async () => {
    if (!selectedFile || loadingAnalysis) {
      return;
    }

    try {
      setLoadingAnalysis(true);
      setAnalysisError("");

      const formData = new FormData();
      formData.append("file", selectedFile);
      if (empresaId !== null && empresaId !== undefined) {
        formData.append("idEmbarcador", empresaId);
      }

      const response = await api.post("/frete/tabelas/preview-pdf", formData);
      setAnalysis(response.data || null);

      addToast({
        place: "tr",
        type: "success",
        title: "PDF analisado",
        description: "Rascunho extraido e pronto para revisao humana.",
        icon: "tim-icons icon-check-2",
      });
    } catch (error) {
      const description = parseBackendError(error);
      setAnalysis(null);
      setAnalysisError(description);
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro na analise",
        description,
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setLoadingAnalysis(false);
    }
  }, [addToast, empresaId, loadingAnalysis, selectedFile]);

  const handleReset = useCallback(() => {
    setSelectedFile(null);
    setSavedTabelaId(null);
    anexoEnviadoRef.current = { id: null };
    setAnalysis(null);
    setAnalysisError("");
    setReviewForm(emptyReviewForm);
    setSelectedTransportadoraId("");
    setEditableRoutes([]);
    setSaveForm({
      nome: "",
      idFreteUnidade: "",
      idModal: "",
      idNegocio: "",
      idTipoServico: "",
      idTipoFrete: "",
      dataValIni: "",
      dataValFim: "",
      origemCidade: "",
      origemUf: "",
    });
    setPrazoExcelFile(null);
    setPrazoImportPreview(null);
    setPrazoImportMapping(createEmptyPrazoImportMapping());
    setPrazoImportedAssignments([]);
    setPrazoImportResult(null);
    setTaxaConfirmations({});
    setTaxaValueOverrides({});
    setTaxaValorRegraOverrides({});
    setExcludedTaxaKeys({});
    setAdditionalChargeConfirmations({});
    setAdditionalChargeValueOverrides({});
    setAdditionalChargeValorRegraOverrides({});
    setExcludedAdditionalChargeKeys({});
    setColumnCalculationModes({});
    setCidadesByUf({});
    setLoadingCidadesByUf({});
    setCapitalSelectionsByUf({});
    setCapitalReviewTouchedByUf({});
    setCitySearchByUf({});
  }, []);

  const handleCarregarUltimoOcr = useCallback(async () => {
    if (loadingUltimoOcr) return;
    try {
      setLoadingUltimoOcr(true);
      const response = await api.get("/frete/tabelas/ultimo-ocr");
      const sessao = response.data;
      // Armazena no ref ANTES do setAnalysis — o useEffect vai ler e consumir
      sessionRestoreRef.current = sessao;
      setAnalysis(sessao.analysis || null);
      addToast({ place: "tr", type: "success", title: "Sessão carregada", description: "Rotas, capitais e metadados restaurados.", icon: "tim-icons icon-check-2" });
    } catch (err) {
      const msg = err.response && err.response.status === 404
        ? "Nenhuma sessão salva. Faça um upload e clique em Salvar Feedback OCR."
        : (err.response && err.response.data && err.response.data.error) || "Erro ao carregar sessão.";
      addToast({ place: "tr", type: "danger", title: "Erro ao carregar", description: msg, icon: "tim-icons icon-alert-circle-exc" });
    } finally {
      setLoadingUltimoOcr(false);
    }
  }, [addToast, loadingUltimoOcr]);

  const handleAddRoute = useCallback(() => {
    const emptyValues = {};
    ROUTE_ADJUST_KEYS.forEach((key) => { emptyValues[key] = ""; });
    setEditableRoutes((prev) => {
      const maxIndex = prev.reduce((m, r) => Math.max(m, r.routeIndex), -1);
      return [
        ...prev,
        { routeIndex: maxIndex + 1, ufDestino: "", routeLabel: "", destinationType: "interior", values: emptyValues },
      ];
    });
  }, []);

  const handleRemoveRoute = useCallback((routeIndex) => {
    setEditableRoutes((prev) => prev.filter((r) => r.routeIndex !== routeIndex));
  }, []);

  const handleRouteUfChange = useCallback((routeIndex, value) => {
    setEditableRoutes((prev) =>
      prev.map((r) => r.routeIndex === routeIndex ? { ...r, ufDestino: value.toUpperCase().slice(0, 2) } : r)
    );
  }, []);

  const handleRouteLabelChange = useCallback((routeIndex, value) => {
    setEditableRoutes((prev) =>
      prev.map((r) => (
        r.routeIndex === routeIndex
          ? { ...r, routeLabel: value, destinationType: inferDestinationType({ ...r, routeLabel: value }) }
          : r
      ))
    );
  }, []);

  const handleRouteValueChange = useCallback((routeIndex, columnKey, value) => {
    setEditableRoutes((prev) =>
      prev.map((r) =>
        r.routeIndex === routeIndex
          ? { ...r, values: { ...r.values, [columnKey]: value } }
          : r
      )
    );
  }, []);

  const handleColumnCalculationModeChange = useCallback((columnKey, mode) => {
    // PO1-3916: PcFx só aceita Fixo.
    if (isPcFx && mode !== "fixed") return;
    setColumnCalculationModes((prev) => ({
      ...prev,
      [columnKey]: COLUMN_CALCULATION_MODES.includes(mode) ? mode : "fixed",
    }));
    if (mode === "per_unit_total") {
      setColumnDivisors((prev) => (prev[columnKey] ? prev : { ...prev, [columnKey]: DEFAULT_COLUMN_DIVISOR }));
    }
  }, [isPcFx]);

  // PO1-3916: ao escolher PcFx, as colunas voltam para Fixo (o que vai no
  // payload é o estado, então ele precisa refletir o travamento).
  useEffect(() => {
    if (!isPcFx) return;
    setColumnCalculationModes((prev) => {
      const temOutro = CUSTO_KEYS.some((key) => prev[key] && prev[key] !== "fixed");
      if (!temOutro) return prev;
      return CUSTO_KEYS.reduce((acc, key) => ({ ...acc, [key]: "fixed" }), { ...prev });
    });
  }, [isPcFx]);

  const handleColumnDivisorChange = useCallback((columnKey, value) => {
    setColumnDivisors((prev) => ({ ...prev, [columnKey]: value }));
  }, []);

  const handlePrazoImportMappingChange = useCallback((field, value) => {
    setPrazoImportMapping((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handlePreviewPrazoExcel = useCallback(async (sheetNameOverride = null) => {
    if (!prazoExcelFile || loadingPrazoExcel) return;
    try {
      setLoadingPrazoExcel(true);
      setPrazoImportedAssignments([]);
      setPrazoImportResult(null);
      const nextMapping = {
        ...prazoImportMapping,
        ...(sheetNameOverride !== null ? { sheetName: sheetNameOverride } : {}),
      };

      const formData = new FormData();
      formData.append("file", prazoExcelFile);
      formData.append("mode", "preview");
      formData.append("routes", JSON.stringify(buildPrazoImportRoutePayload(editableRoutes)));
      if (nextMapping.sheetName) {
        formData.append("sheetName", nextMapping.sheetName);
      }
      formData.append("mapping", JSON.stringify({
        ufColumnIndex: nextMapping.ufColumnIndex,
        cidadeColumnIndex: nextMapping.cidadeColumnIndex,
        rotaColumnIndex: nextMapping.rotaColumnIndex,
        prazoColumnIndex: nextMapping.prazoColumnIndex,
      }));

      const res = await api.post("/frete/tabelas/prazo-excel", formData);
      const workbook = res.data?.workbook || null;
      const preview = res.data?.preview || null;

      setPrazoImportPreview(res.data || null);
      setPrazoImportMapping(buildPrazoImportMappingFromWorkbook(workbook));

      if (preview) {
        addToast({
          place: "tr",
          type: "info",
          title: "Planilha lida",
          description: `${preview.matchedRows} linhas bateram com rotas e ${preview.unmatchedRows} ficaram pendentes.`,
          icon: "tim-icons icon-check-2",
        });
      }
    } catch (err) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro ao ler planilha",
        description: err?.response?.data?.error || err.message,
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setLoadingPrazoExcel(false);
    }
  }, [addToast, editableRoutes, loadingPrazoExcel, prazoExcelFile, prazoImportMapping]);

  const handleApplyPrazoExcel = useCallback(async () => {
    if (!prazoExcelFile || loadingPrazoExcel) return;
    try {
      setLoadingPrazoExcel(true);

      const formData = new FormData();
      formData.append("file", prazoExcelFile);
      formData.append("mode", "apply");
      formData.append("routes", JSON.stringify(buildPrazoImportRoutePayload(editableRoutes)));
      if (prazoImportMapping.sheetName) {
        formData.append("sheetName", prazoImportMapping.sheetName);
      }
      formData.append("mapping", JSON.stringify({
        ufColumnIndex: prazoImportMapping.ufColumnIndex,
        cidadeColumnIndex: prazoImportMapping.cidadeColumnIndex,
        rotaColumnIndex: prazoImportMapping.rotaColumnIndex,
        prazoColumnIndex: prazoImportMapping.prazoColumnIndex,
      }));

      const res = await api.post("/frete/tabelas/prazo-excel", formData);
      const workbook = res.data?.workbook || null;
      const result = res.data?.result || null;
      const assignments = Array.isArray(result?.assignments) ? result.assignments : [];
      const routeSummary = Array.isArray(result?.routeSummary) ? result.routeSummary : [];

      setPrazoImportPreview(res.data || null);
      setPrazoImportMapping(buildPrazoImportMappingFromWorkbook(workbook));
      setPrazoImportedAssignments(assignments);
      setPrazoImportResult(result);

      if (routeSummary.length > 0) {
        const prazoByRouteIndex = routeSummary.reduce((acc, item) => {
          if (item?.routeIndex === null || item?.routeIndex === undefined) {
            return acc;
          }

          if (Array.isArray(item.prazoValues) && item.prazoValues.length === 1) {
            acc[String(item.routeIndex)] = String(item.prazoValues[0]);
          }
          return acc;
        }, {});

        if (Object.keys(prazoByRouteIndex).length > 0) {
          setEditableRoutes((prev) =>
            prev.map((route) => {
              const prazoValue = prazoByRouteIndex[String(route.routeIndex)];
              if (prazoValue === undefined) {
                return route;
              }

              return {
                ...route,
                values: {
                  ...route.values,
                  prazo_entrega: prazoValue,
                },
              };
            })
          );
        }
      }

      addToast({
        place: "tr",
        type: "success",
        title: "Prazos aplicados",
        description: `${result?.matchedRows || 0} cidades vinculadas às rotas; ${result?.unmatchedRows || 0} linhas ficaram sem casamento.`,
        icon: "tim-icons icon-check-2",
      });
    } catch (err) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro ao aplicar prazos",
        description: err?.response?.data?.error || err.message,
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setLoadingPrazoExcel(false);
    }
  }, [addToast, editableRoutes, loadingPrazoExcel, prazoExcelFile, prazoImportMapping]);

  // TASK-876: confirmar matching de taxa
  const handleTaxaConfirmation = useCallback((columnKey, fretaTaxaId) => {
    setTaxaConfirmations((prev) => ({ ...prev, [columnKey]: fretaTaxaId }));
    setTaxaValorRegraOverrides((prev) => {
      if (prev[columnKey] === undefined) {
        return prev;
      }

      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
  }, []);

  const handleToggleTaxaExclusion = useCallback((columnKey) => {
    setExcludedTaxaKeys((prev) => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
  }, []);

  const handleAdditionalChargeConfirmation = useCallback((chargeKey, fretaTaxaId) => {
    setAdditionalChargeConfirmations((prev) => ({ ...prev, [chargeKey]: fretaTaxaId }));
    setAdditionalChargeValorRegraOverrides((prev) => {
      if (prev[chargeKey] === undefined) {
        return prev;
      }

      const next = { ...prev };
      delete next[chargeKey];
      return next;
    });
  }, []);

  const handleToggleAdditionalChargeExclusion = useCallback((chargeKey) => {
    setExcludedAdditionalChargeKeys((prev) => ({
      ...prev,
      [chargeKey]: !prev[chargeKey],
    }));
  }, []);

  // TASK-874: salvar feedback OCR + sessão completa
  const handleSaveFeedback = useCallback(async () => {
    if (!structuredJsonPreview || loadingFeedback) return;
    try {
      setLoadingFeedback(true);

      const analysisComEdits = analysis ? {
        ...analysis,
        draft: {
          ...analysis.draft,
          transportadora: { nome: reviewForm.transportadoraNome, cnpj: reviewForm.transportadoraCnpj },
          cliente: { nome: reviewForm.clienteNome, cnpj: reviewForm.clienteCnpj },
          tituloTabela: reviewForm.tituloTabela,
          origem: { cidade: reviewForm.origemCidade, uf: reviewForm.origemUf },
          vigencia: { ...analysis.draft?.vigencia, inicio: reviewForm.vigenciaInicio, fim: reviewForm.vigenciaFim },
        },
      } : null;

      await api.post("/frete/tabelas/feedback-ocr", {
        structuredJson: structuredJsonPreview,
        fileName: analysis?.meta?.fileName || selectedFile?.name || null,
        observacao: null,
        sessao: analysisComEdits ? {
          analysis: analysisComEdits,
          editableRoutes,
          saveForm,
          selectedTransportadoraId,
          taxaConfirmations,
          taxaValueOverrides,
          taxaValorRegraOverrides,
          excludedTaxaKeys,
          additionalChargeConfirmations,
          additionalChargeValueOverrides,
          additionalChargeValorRegraOverrides,
          excludedAdditionalChargeKeys,
          capitalSelectionsByUf,
          columnCalculationModes,
          prazoImportPreview,
          prazoImportMapping,
          prazoImportedAssignments,
          prazoImportResult,
        } : undefined,
      });
      addToast({
        place: "tr",
        type: "success",
        title: "Feedback salvo",
        description: "As correções foram registradas para melhorias futuras.",
        icon: "tim-icons icon-check-2",
      });
    } catch (err) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro ao salvar feedback",
        description: err?.response?.data?.error || err.message,
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setLoadingFeedback(false);
    }
  }, [addToast, additionalChargeConfirmations, additionalChargeValueOverrides, additionalChargeValorRegraOverrides, analysis, capitalSelectionsByUf, columnCalculationModes, editableRoutes, excludedAdditionalChargeKeys, excludedTaxaKeys, loadingFeedback, prazoImportMapping, prazoImportPreview, prazoImportResult, prazoImportedAssignments, reviewForm, saveForm, selectedFile, selectedTransportadoraId, structuredJsonPreview, taxaConfirmations, taxaValueOverrides, taxaValorRegraOverrides]);

  const handleSalvarTabela = useCallback(async () => {
    if (!structuredJsonPreview || isSavingRef.current) return;
    isSavingRef.current = true;

    const failSave = (description, title = "Campo obrigatório") => {
      isSavingRef.current = false;
      addToast({ place: "tr", type: "danger", title, description, icon: "tim-icons icon-alert-circle-exc" });
    };

    if (!empresaId) return failSave("Selecione um embarcador.");
    if (!selectedTransportadoraId) return failSave("Selecione a transportadora.");
    if (!saveForm.idFreteUnidade) return failSave("Selecione a Unidade de Frete.");
    if (!saveForm.idModal) return failSave("Selecione o Modal.");
    if (!saveForm.idNegocio) return failSave("Selecione o Negócio.");
    if (!saveForm.idTipoServico) return failSave("Selecione o Tipo de Serviço.");
    if (!saveForm.idTipoFrete) return failSave("Selecione o Tipo de Frete.");
    if (!saveForm.origemUf) return failSave("Informe a UF de origem.");
    if (!saveForm.origemCidade) return failSave("Informe a cidade de origem.");
    if (freteTabelaCustoPreview.summary.pendingGroupCount > 0) {
      return failSave("Resolva as UFs com capital/interior ambíguos antes de salvar a tabela.", "Pendências na distribuição");
    }

    if (resolvedLinePreparation.conflicts.length > 0) {
      const conflictSample = resolvedLinePreparation.conflicts
        .slice(0, 3)
        .map((item) => `${item.ufDestino}/${item.cidadeDestino} (${item.routeAnterior} x ${item.routeAtual})`)
        .join(", ");

      return failSave(
        `A mesma cidade caiu em rotas com valores diferentes: ${conflictSample}.`,
        "Conflito de rotas"
      );
    }

    const routes = structuredJsonPreview.tableModel?.routes || [];
    const rotas = routes.map((r) => ({
      ufDestino: r.ufDestino,
      custos: {
        ate_75_kg: r.columnMap?.ate_75_kg ?? r.values?.ate_75_kg ?? null,
        de_76_a_100_kg: r.columnMap?.de_76_a_100_kg ?? r.values?.de_76_a_100_kg ?? null,
        acima_100_kg: r.columnMap?.acima_100_kg ?? r.values?.acima_100_kg ?? null,
      },
    }));

    const linhasResolvidas = resolvedLinePreparation.rows.map((line) => ({
      ufDestino: line.ufDestino,
      tipoCobertura: line.tipoCobertura,
      routeLabel: line.routeLabel,
      routeIndex: line.routeIndex,
      idCidadeDestino: line.idCidadeDestino ? parseInt(line.idCidadeDestino, 10) : null,
      cidadeDestino: line.cidadeDestino || null,
      custos: CUSTO_KEYS.reduce((acc, key) => ({
        ...acc,
        [key]: toNumericValue(line.values?.[key]),
      }), {}),
      taxas: TAXA_KEYS.reduce((acc, key) => ({
        ...acc,
        [key]: excludedTaxaKeys[key]
          ? null
          : taxaValueOverrides[key] !== undefined
            ? toNumericValue(taxaValueOverrides[key])
            : toNumericValue(line.values?.[key]),
      }), {}),
    }));

    if (linhasResolvidas.length === 0) {
      return failSave("Nao foi possivel montar nenhuma linha de custo por cidade para persistir.", "Sem linhas resolvidas");
    }

    const missingTaxaMappings = effectiveTaxaConfigs
      .filter((config) => !config.excluded && config.possuiValorConfigurado && !config.idFretaTaxa)
      .map((config) => COL_SHORT[config.columnKey] || config.columnKey);

    const missingAdditionalChargeMappings = effectiveAdditionalChargeConfigs
      .filter((config) => !config.excluded && config.possuiValorDetectado && !config.idFretaTaxa)
      .map((config) => config.label || config.itemKey);

    const allMissingTaxaMappings = [...missingTaxaMappings, ...missingAdditionalChargeMappings];

    if (allMissingTaxaMappings.length > 0) {
      return failSave(`Selecione o tipo de taxa para: ${allMissingTaxaMappings.join(", ")}.`, "Taxas sem mapeamento");
    }

    const taxasConfiguradas = effectiveTaxaConfigs
      .filter((config) => !config.excluded && config.idFretaTaxa)
      .map((config) => ({
        columnKey: config.columnKey,
        idFretaTaxa: config.idFretaTaxa,
        valorRegra: config.valorRegra,
      }));

    const taxas = effectiveAdditionalChargeConfigs
      .filter((config) => !config.excluded && config.idFretaTaxa && config.valorGlobal !== null && config.valorGlobal !== undefined)
      .map((config) => ({
        key: config.itemKey,
        label: config.label,
        idFretaTaxa: config.idFretaTaxa,
        valorRegra: config.valorRegra,
        valor: config.valorGlobal,
        source: config.source,
      }));

    // PO1-3916: em PcFx não entram as faixas padrão em kg.
    const weightTiers = CUSTO_KEYS.map((key) => {
      const column = getColumnDefinition(
        structuredJsonPreview.tableModel?.columnsExpected || currentColumnsExpected,
        key,
        { pcFx: isPcFx }
      );
      const weightRange = getColumnWeightRange(
        structuredJsonPreview.tableModel?.columnsExpected || currentColumnsExpected,
        key,
        { pcFx: isPcFx }
      );

      return {
        key,
        label: column?.label || key,
        valUnidadeInicial: weightRange.valUnidadeInicial,
        valUnidadeFinal: weightRange.valUnidadeFinal,
      };
    });

    const savePayload = {
      nome: saveForm.nome || structuredJsonPreview.header?.tituloTabela || "",
      idEmbarcador: empresaId,
      idTransportadora: selectedTransportadoraId,
      idFreteUnidade: saveForm.idFreteUnidade,
      idModal: saveForm.idModal,
      idNegocio: saveForm.idNegocio,
      idTipoServico: saveForm.idTipoServico,
      idTipoFrete: saveForm.idTipoFrete,
      dataValIni: saveForm.dataValIni || null,
      dataValFim: saveForm.dataValFim || null,
      origemUf: saveForm.origemUf,
      origemCidade: saveForm.origemCidade,
      rotas,
      linhasResolvidas,
      taxasConfiguradas,
      weightTiers,
      columnCalculationModes,
      columnDivisors,
      taxas,
    };

    const payloadSignature = buildSavePayloadSignature(savePayload);
    if (saveRequestRef.current.signature !== payloadSignature) {
      saveRequestRef.current = {
        id: createClientRequestId(),
        signature: payloadSignature,
      };
    } else if (!saveRequestRef.current.id) {
      saveRequestRef.current = {
        id: createClientRequestId(),
        signature: payloadSignature,
      };
    }

    try {
      setLoadingSalvar(true);
      const response = await api.post("/frete/tabelas/salvar-ocr", {
        ...savePayload,
        clientRequestId: saveRequestRef.current.id,
      });
      saveRequestRef.current = { id: null, signature: null };
      addToast({
        place: "tr", type: "success", title: "Tabela salva",
        description: `ID ${response.data.id} — ${response.data.custosInseridos} custos, ${response.data.taxasInseridas} taxas.`,
        icon: "tim-icons icon-check-2",
      });

      // PO1-2859: o PDF analisado vira o anexo original da tabela (best-effort; o salvar
      // não é desfeito se o anexo falhar). Resposta reaproveitada (clientRequestId) não reenvia.
      const idSalvo = response.data.id;
      if (idSalvo && selectedFile && anexoEnviadoRef.current.id !== idSalvo) {
        try {
          await enviarAnexos(idSalvo, [selectedFile]);
          anexoEnviadoRef.current = { id: idSalvo };
          addToast({
            place: "tr", type: "success", title: "PDF anexado",
            description: `${selectedFile.name} guardado como anexo original da tabela ${idSalvo}.`,
            icon: "tim-icons icon-attach-87",
          });
        } catch (anexoErr) {
          addToast({
            place: "tr", type: "warning", title: "Tabela salva, PDF não anexado",
            description: mensagemErroAnexo(anexoErr, "Não foi possível anexar o PDF à tabela."),
            icon: "tim-icons icon-alert-circle-exc",
          });
        }
      }
      // O bloco de anexos só monta depois do auto-anexo: se montasse antes, a lista seria
      // buscada antes do POST terminar e apareceria vazia (achado do QA de 05/09).
      setSavedTabelaId(idSalvo || null);
    } catch (err) {
      const message = (err.response && err.response.data && err.response.data.error) || err.message || "Erro ao salvar tabela.";
      const saveInProgress = isSaveInProgressResponse(err, message);
      const timeoutLike = isRequestTimeoutLikeError(err, message);

      if (saveInProgress) {
        addToast({
          place: "tr",
          type: "warning",
          title: "Salvamento em andamento",
          description: "Esta tabela OCR já está sendo salva. Aguarde a conclusão antes de tentar novamente.",
          icon: "tim-icons icon-alert-circle-exc",
        });
      } else if (timeoutLike) {
        addToast({
          place: "tr",
          type: "warning",
          title: "Resposta demorada",
          description: "O backend pode ainda estar processando o salvamento. Se precisar tentar de novo, o mesmo pedido será reaproveitado.",
          icon: "tim-icons icon-alert-circle-exc",
        });
      } else {
        saveRequestRef.current = { id: null, signature: null };
        addToast({
          place: "tr",
          type: "danger",
          title: "Erro ao salvar",
          description: message,
          icon: "tim-icons icon-alert-circle-exc",
        });
      }
    } finally {
      isSavingRef.current = false;
      setLoadingSalvar(false);
    }
  }, [
    addToast, columnCalculationModes, effectiveAdditionalChargeConfigs, effectiveTaxaConfigs, empresaId, freteTabelaCustoPreview,
    currentColumnsExpected, isPcFx, resolvedLinePreparation, saveForm, selectedTransportadoraId, structuredJsonPreview,
    excludedTaxaKeys, taxaValueOverrides, selectedFile,
  ]);

  // TASK-875: wizard handlers
  const handleOpenWizard = useCallback(() => {
    setWizardForm({
      cnpj: reviewForm.transportadoraCnpj || "",
      razaoSocial: reviewForm.transportadoraNome || "",
      fantasia: reviewForm.transportadoraNome || "",
      cep: "",
    });
    setWizardStep(1);
    setShowWizard(true);
  }, [reviewForm.transportadoraCnpj, reviewForm.transportadoraNome]);

  const handleWizardCnpjLookup = useCallback(async () => {
    const cnpj = wizardForm.cnpj.replace(/\D/g, "");
    if (!cnpj || cnpj.length !== 14) {
      addToast({ place: "tr", type: "warning", title: "CNPJ inválido", description: "Informe 14 dígitos.", icon: "tim-icons icon-alert-circle-exc" });
      return;
    }
    try {
      setLoadingCnpj(true);
      const res = await api.get(`/frete/tabelas/cnpj-lookup/${cnpj}`);
      const d = res.data;
      setWizardForm((prev) => ({
        ...prev,
        razaoSocial: d.razao_social || prev.razaoSocial,
        fantasia: d.nome_fantasia || d.razao_social || prev.fantasia,
        cep: d.cep ? d.cep.replace(/\D/g, "") : prev.cep,
      }));
      addToast({ place: "tr", type: "success", title: "CNPJ encontrado", description: d.razao_social || "Dados preenchidos.", icon: "tim-icons icon-check-2" });
    } catch (err) {
      addToast({ place: "tr", type: "warning", title: "CNPJ não encontrado", description: err?.response?.data?.error || err.message, icon: "tim-icons icon-alert-circle-exc" });
    } finally {
      setLoadingCnpj(false);
    }
  }, [addToast, wizardForm.cnpj]);

  const handleWizardSave = useCallback(async () => {
    try {
      setLoadingWizardSave(true);
      const res = await api.post("/frete/tabelas/transportadora", {
        cnpj: wizardForm.cnpj.replace(/\D/g, ""),
        razaoSocial: wizardForm.razaoSocial,
        fantasia: wizardForm.fantasia || wizardForm.razaoSocial,
        cep: wizardForm.cep,
        idEmbarcador: empresaId,
        idTipoServico: wizardSelectedTipoServico || null,
      });
      // PO1-3283: pela porta única o backend pode responder 202 (curadoria da
      // Everlog) ou devolver o estado da esteira (`onboarding`/`estado`).
      const d = res.data || {};
      const emCuradoria = res.status === 202 || d.estado === "EM_CURADORIA" || (d.curadoria && !d.transportadora);
      const estado = d.onboarding || d.estado || null;
      const ESTADO_LABEL = { AGUARDANDO_HOMOLOGACAO: "aguardando homologação da Everlog", EM_CONFIGURACAO: "aguardando homologação da Everlog", EM_ONBOARDING: "em onboarding", OPERACIONAL: "operacional" };
      addToast({
        place: "tr",
        type: emCuradoria ? "warning" : "success",
        title: emCuradoria ? "Transportadora aguardando a Everlog" : d.criada ? "Transportadora cadastrada" : "Transportadora vinculada",
        description: emCuradoria
          ? d.message || (d.curadoria && d.curadoria.motivo) || "A matriz precisa de ajuste pela Everlog; o vínculo nasce quando ela liberar."
          : [d.message, estado && ESTADO_LABEL[estado] ? `Esteira: ${ESTADO_LABEL[estado]}.` : null].filter(Boolean).join(" "),
        icon: emCuradoria ? "tim-icons icon-alert-circle-exc" : "tim-icons icon-check-2",
      });
      setShowWizard(false);
      loadTransportadoras();
    } catch (err) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro ao cadastrar",
        description: err?.response?.data?.error || err.message,
        icon: "tim-icons icon-simple-remove",
      });
    } finally {
      setLoadingWizardSave(false);
    }
  }, [addToast, empresaId, loadTransportadoras, wizardForm, wizardSelectedTipoServico]);

  // Carregar tipos de serviço ao abrir o wizard
  useEffect(() => {
    if (!showWizard || tiposServico.length > 0) return;
    api.get("/frete/tipos-servico").then((res) => {
      setTiposServico(Array.isArray(res.data) ? res.data : []);
    }).catch(() => {});
  }, [showWizard, tiposServico.length]);

  const prazoWorkbook = prazoImportPreview?.workbook || null;
  const prazoPreviewSummary = prazoImportPreview?.preview || null;
  const prazoWorkbookSheets = Array.isArray(prazoWorkbook?.sheets) ? prazoWorkbook.sheets : [];
  const prazoColumnOptions = Array.isArray(prazoWorkbook?.columnOptions) ? prazoWorkbook.columnOptions : [];
  const prazoImportRouteSummary = Array.isArray(prazoImportResult?.routeSummary) ? prazoImportResult.routeSummary : [];
  const prazoUnmatchedSamples =
    Array.isArray(prazoImportResult?.unmatchedSamples) && prazoImportResult.unmatchedSamples.length > 0
      ? prazoImportResult.unmatchedSamples
      : (Array.isArray(prazoPreviewSummary?.unmatchedSamples) ? prazoPreviewSummary.unmatchedSamples : []);

  return (
    <div className="content">
      <><Card style={cardStyle}>
        <CardBody>
          <Row style={{ alignItems: "center", marginBottom: "1rem" }}>
            <Col md="8">
              <CardTitle tag="h4" style={{ marginBottom: "0.35rem" }}>
                <i className="tim-icons icon-upload" /> Nova Tabela de Frete
              </CardTitle>
              <div style={{ color: "#9ba6d1", fontSize: "0.86rem" }}>
                Fluxo de testes para upload do PDF, extracao preliminar e revisao humana antes da consolidacao do JSON estruturado.
              </div>
            </Col>
            <Col
              md="4"
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "0.5rem",
                flexWrap: "wrap",
              }}
            >
              <Button color="secondary" size="sm" onClick={() => history.push("/app/cadastros/tabelas-frete")}>
                Voltar para Tabelas
              </Button>
              <Badge color="info" style={{ alignSelf: "center", padding: "0.55rem 0.8rem" }}>
                Teste
              </Badge>
            </Col>
          </Row>

          <div
            style={{
              marginBottom: "1rem",
              padding: "0.9rem 1rem",
              borderRadius: 10,
              border: "1px solid #2b3553",
              background: "rgba(20, 27, 46, 0.58)",
            }}
          >
            <div style={{ color: "#8f9bc7", fontSize: "0.72rem", marginBottom: "0.25rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
              Contexto Atual
            </div>
            <div style={{ color: "#f5f7fb", fontSize: "0.88rem" }}>
              Embarcador: {embarcadorLabel}
            </div>
            <div style={{ color: "#c7d3fb", fontSize: "0.82rem", marginTop: "0.15rem" }}>
              Transportadoras disponiveis para vinculo: {loadingTransportadoras ? "carregando..." : transportadoraOptions.length}
            </div>
          </div>

          <Row>
            <Col lg="4" md="12" style={{ marginBottom: "1rem" }}>
              <div style={panelStyle}>
                <div style={sectionTitleStyle}>1. Upload do PDF</div>
                <p style={bodyTextStyle}>
                  O cadastro novo comeca pelo documento. A transportadora pode ser identificada no PDF e vinculada depois, mesmo sem estar no contexto da tela.
                </p>
                <FormGroup style={{ marginTop: "1rem", marginBottom: "0.75rem" }}>
                  <Label style={{ color: "#dbe7ff" }}>Arquivo PDF</Label>
                  {/* PO1-3887: o tema esconde o input file dentro de .form-group. */}
                  <EscolherArquivos
                    accept=".pdf,application/pdf"
                    disabled={loadingAnalysis}
                    ariaLabel="Arquivo PDF"
                    rotulo="Escolher PDF"
                    onSelecionar={(files) => setSelectedFile(files && files.length ? files[0] : null)}
                  />
                  {selectedFile ? (
                    <small className="d-block mt-2" style={{ color: "#8f9bc7" }}>
                      Selecionado: {selectedFile.name}
                    </small>
                  ) : null}
                </FormGroup>

                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginTop: "1rem" }}>
                  <Button color="info" size="sm" disabled={!selectedFile || loadingAnalysis} onClick={handleAnalyze}>
                    {loadingAnalysis ? <Spinner size="sm" /> : "Analisar PDF"}
                  </Button>
                  <Button color="secondary" size="sm" outline disabled={loadingAnalysis} onClick={handleReset}>
                    Limpar
                  </Button>
                  <Button color="secondary" size="sm" outline disabled={loadingUltimoOcr} onClick={handleCarregarUltimoOcr}>
                    {loadingUltimoOcr ? <Spinner size="sm" /> : "Carregar último OCR"}
                  </Button>
                </div>

                {analysisError ? (
                  <div
                    style={{
                      marginTop: "1rem",
                      borderRadius: 8,
                      border: "1px solid #e74c3c",
                      background: "rgba(231,76,60,0.12)",
                      color: "#ffb1ab",
                      fontSize: "0.82rem",
                      padding: "0.75rem 0.85rem",
                    }}
                  >
                    {analysisError}
                  </div>
                ) : null}
              </div>
            </Col>

            <Col lg="4" md="6" style={{ marginBottom: "1rem" }}>
              <div style={panelStyle}>
                <div style={sectionTitleStyle}>2. Extracao</div>
                <p style={bodyTextStyle}>
                  O backend gera um rascunho com OCR e parsing heuristico: transportadora, cliente, vigencia, percursos, colunas e regras comerciais. Quando configurado, o OCR pode ser executado via MCP/OpenAI com fallback local.
                </p>
                <div style={{ marginTop: "1rem" }}>
                  <InfoBlock
                    label="Status"
                    value={analysis ? "Rascunho extraido" : "Aguardando upload"}
                    color={analysis ? "#2ecc71" : "#dbe7ff"}
                  />
                  <InfoBlock
                    label="Metodo"
                    value={analysis?.meta?.extractionMode || "Aguardando analise"}
                  />
                  <InfoBlock
                    label="Texto extraido"
                    value={
                      analysis?.meta?.rawTextLength
                        ? `${analysis.meta.rawTextLength} caracteres`
                        : "-"
                    }
                  />
                </div>
              </div>
            </Col>

            <Col lg="4" md="6" style={{ marginBottom: "1rem" }}>
              <div style={panelStyle}>
                <div style={sectionTitleStyle}>3. Revisao Humana</div>
                <p style={bodyTextStyle}>
                  O operador revisa a transportadora candidata, confere vigencia e analisa percursos, taxas e pendencias antes de qualquer gravacao.
                </p>
                <div style={{ marginTop: "1rem" }}>
                  <InfoBlock label="Match da transportadora" value={matchingStatus.label} />
                  <InfoBlock
                    label="Persistencia final"
                    value="Ainda nao implementada neste menu de testes"
                    color="#f7c46c"
                  />
                </div>
              </div>
            </Col>
          </Row>

          {analysis ? (
            <>
              <div
                style={{
                  border: "1px solid #2b3553",
                  borderRadius: 10,
                  padding: "1rem 1.1rem",
                  background: "rgba(19, 28, 48, 0.72)",
                  marginBottom: "1rem",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
                  <div>
                    <div style={{ color: "#dbe7ff", fontWeight: 600, marginBottom: "0.25rem" }}>
                      Rascunho para revisao
                    </div>
                    <div style={{ color: "#8f9bc7", fontSize: "0.82rem" }}>
                      Arquivo analisado: {analysis?.meta?.fileName || selectedFile?.name || "-"}
                    </div>
                  </div>
                  <MatchingBadge status={matchingStatus.status} label={matchingStatus.label} />
                </div>

                {Array.isArray(analysis.warnings) && analysis.warnings.length ? (
                  <div style={{ marginBottom: "0.35rem" }}>
                    {analysis.warnings.map((warning) => (
                      <div
                        key={warning}
                        style={{
                          border: "1px solid #f39c12",
                          background: "rgba(243,156,18,0.1)",
                          color: "#ffd28a",
                          borderRadius: 8,
                          padding: "0.7rem 0.85rem",
                          marginBottom: "0.5rem",
                          fontSize: "0.81rem",
                        }}
                      >
                        {warning}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>

              <Row>
                <Col lg="8" style={{ marginBottom: "1rem" }}>
                  <div style={panelStyle}>
                    <div style={sectionTitleStyle}>Cabecalho Revisavel</div>
                    <Row>
                      <Col md="6">
                        <FormGroup>
                          <Label style={{ color: "#dbe7ff" }}>Transportadora identificada</Label>
                          <Input
                            value={reviewForm.transportadoraNome}
                            onChange={(event) => handleFieldChange("transportadoraNome", event.target.value)}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <Label style={{ color: "#dbe7ff" }}>CNPJ da transportadora</Label>
                          <Input
                            value={reviewForm.transportadoraCnpj}
                            onChange={(event) => handleFieldChange("transportadoraCnpj", event.target.value)}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <Label style={{ color: "#dbe7ff" }}>Cliente identificado</Label>
                          <Input
                            value={reviewForm.clienteNome}
                            onChange={(event) => handleFieldChange("clienteNome", event.target.value)}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <Label style={{ color: "#dbe7ff" }}>CNPJ do cliente</Label>
                          <Input
                            value={reviewForm.clienteCnpj}
                            onChange={(event) => handleFieldChange("clienteCnpj", event.target.value)}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="12">
                        <FormGroup>
                          <Label style={{ color: "#dbe7ff" }}>Titulo da tabela</Label>
                          <Input
                            value={reviewForm.tituloTabela}
                            onChange={(event) => handleFieldChange("tituloTabela", event.target.value)}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="3">
                        <FormGroup>
                          <Label style={{ color: "#dbe7ff" }}>Vigencia inicio</Label>
                          <Input
                            value={reviewForm.vigenciaInicio}
                            onChange={(event) => handleFieldChange("vigenciaInicio", event.target.value)}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="3">
                        <FormGroup>
                          <Label style={{ color: "#dbe7ff" }}>Vigencia fim</Label>
                          <Input
                            value={reviewForm.vigenciaFim}
                            onChange={(event) => handleFieldChange("vigenciaFim", event.target.value)}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup>
                          <Label style={{ color: "#dbe7ff" }}>Origem cidade</Label>
                          <Input
                            value={reviewForm.origemCidade}
                            onChange={(event) => handleFieldChange("origemCidade", event.target.value)}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="2">
                        <FormGroup>
                          <Label style={{ color: "#dbe7ff" }}>UF</Label>
                          <Input
                            value={reviewForm.origemUf}
                            onChange={(event) => handleFieldChange("origemUf", event.target.value)}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>
                </Col>

                <Col lg="4" style={{ marginBottom: "1rem" }}>
                  <div style={panelStyle}>
                    <div style={sectionTitleStyle}>Vinculo da Transportadora</div>
                    <div style={{ ...bodyTextStyle, marginBottom: "0.85rem" }}>
                      O documento pode apontar uma transportadora que ainda nao esteja cadastrada. Use este campo para revisar o vinculo no embarcador atual.
                    </div>

                    <FormGroup>
                      <Label style={{ color: "#dbe7ff" }}>Transportadora cadastrada</Label>
                      <Input
                        type="select"
                        value={selectedTransportadoraId}
                        onChange={(event) => setSelectedTransportadoraId(event.target.value)}
                      >
                        <option value="">Nao encontrada / cadastrar depois</option>
                        {transportadoraOptions.map((option) => {
                          const value = getOptionValue(option);
                          const label = getOptionLabel(option);
                          return (
                            <option key={`${value || label}`} value={value !== null && value !== undefined ? String(value) : ""}>
                              {label}
                            </option>
                          );
                        })}
                      </Input>
                    </FormGroup>

                    <InfoBlock label="Status do match" value={matchingStatus.label} />
                    <InfoBlock
                      label="Sugestao atual"
                      value={matchedTransportadora ? getOptionLabel(matchedTransportadora) : "Nenhuma"}
                    />
                    <InfoBlock
                      label="Acao sugerida"
                      value={transportadoraResolution.label}
                      color={transportadoraResolution.mode === "suggest_create" ? "#f7c46c" : "#dbe7ff"}
                    />

                    {transportadoraResolution.suggestedCreate ? (
                      <div
                        style={{
                          marginTop: "0.8rem",
                          border: "1px solid #f39c12",
                          borderRadius: 8,
                          background: "rgba(243,156,18,0.08)",
                          padding: "0.7rem 0.8rem",
                        }}
                      >
                        <div style={{ ...fieldLabelStyle, marginBottom: "0.25rem" }}>
                          Sugestao de criacao
                        </div>
                        <div style={{ color: "#f7d090", fontSize: "0.8rem", lineHeight: 1.45 }}>
                          Nome: {transportadoraResolution.suggestedCreate.nome || "-"}
                        </div>
                        <div style={{ color: "#f7d090", fontSize: "0.8rem", lineHeight: 1.45 }}>
                          CNPJ: {transportadoraResolution.suggestedCreate.cnpj || "-"}
                        </div>
                        {/* TASK-875: wizard de cadastro */}
                        <Button
                          color="warning"
                          size="sm"
                          style={{ marginTop: "0.6rem" }}
                          onClick={handleOpenWizard}
                        >
                          Cadastrar transportadora
                        </Button>
                      </div>
                    ) : null}

                    {Array.isArray(analysis?.draft?.pendencias) && analysis.draft.pendencias.length ? (
                      <div style={{ marginTop: "1rem" }}>
                        <div style={fieldLabelStyle}>Pendencias identificadas</div>
                        {analysis.draft.pendencias.map((item) => (
                          <div
                            key={item}
                            style={{
                              border: "1px dashed #f39c12",
                              color: "#ffd28a",
                              borderRadius: 8,
                              padding: "0.65rem 0.75rem",
                              fontSize: "0.79rem",
                              marginBottom: "0.45rem",
                            }}
                          >
                            {item}
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </Col>
              </Row>

              <Row>
                <Col lg="8" style={{ marginBottom: "1rem" }}>
                  <div style={panelStyle}>
                    <div style={sectionTitleStyle}>Percursos Identificados</div>
                    {Array.isArray(analysis?.draft?.percursos) && analysis.draft.percursos.length ? (
                      <div className="table-responsive">
                        <Table dark style={{ marginBottom: 0 }}>
                          <thead>
                            <tr>
                              <th>UF</th>
                              <th>Percurso</th>
                              <th>Metricas OCR</th>
                            </tr>
                          </thead>
                          <tbody>
                            {analysis.draft.percursos.map((route, index) => (
                              <tr key={`${route.uf}-${route.descricao}-${index}`}>
                                <td>{route.uf}</td>
                                <td>{route.descricao}</td>
                                <td>{Array.isArray(route.metricas) ? route.metricas.join(" | ") : "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      <div style={bodyTextStyle}>Nenhum percurso extraido.</div>
                    )}
                  </div>
                </Col>

                <Col lg="4" style={{ marginBottom: "1rem" }}>
                  <div style={panelStyle}>
                    <div style={sectionTitleStyle}>Colunas e Regras</div>
                    <div style={{ marginBottom: "1rem" }}>
                      <div style={fieldLabelStyle}>Colunas detectadas</div>
                      {Array.isArray(analysis?.draft?.colunasIdentificadas) && analysis.draft.colunasIdentificadas.length ? (
                        analysis.draft.colunasIdentificadas.map((item) => (
                          <Chip key={item.key}>{item.label}</Chip>
                        ))
                      ) : (
                        <div style={bodyTextStyle}>Nenhuma coluna detectada.</div>
                      )}
                    </div>

                    <div style={{ marginBottom: "1rem" }}>
                      <div style={fieldLabelStyle}>Resumo de regras</div>
                      {Array.isArray(analysis?.draft?.resumoRegras) && analysis.draft.resumoRegras.length ? (
                        analysis.draft.resumoRegras.map((item) => (
                          <Chip key={item.nome} color="#2ecc71" background="rgba(46,204,113,0.12)">
                            {item.nome}
                          </Chip>
                        ))
                      ) : (
                        <div style={bodyTextStyle}>Nenhuma regra resumida.</div>
                      )}
                    </div>

                    <div>
                      <div style={fieldLabelStyle}>Lista OCR de regras</div>
                      {Array.isArray(analysis?.draft?.regrasDetectadas) && analysis.draft.regrasDetectadas.length ? (
                        analysis.draft.regrasDetectadas.map((item) => (
                          <div key={item.nome} style={{ color: "#dbe7ff", fontSize: "0.81rem", marginBottom: "0.35rem" }}>
                            {item.nome}
                          </div>
                        ))
                      ) : (
                        <div style={bodyTextStyle}>Sem regras listadas.</div>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>

              {/* TASK-876: Separação Custos vs Taxas */}
              {(analysis?.taxaAnalysis || detectedAdditionalCharges.length > 0) && (
                <Row>
                  <Col lg="12" style={{ marginBottom: "1rem" }}>
                    <div style={panelStyle}>
                      <div style={{ ...sectionTitleStyle, marginBottom: "0.9rem" }}>Separação Custos vs Taxas</div>

                      <Row>
                        <Col md="4" style={{ marginBottom: "0.75rem" }}>
                          <div style={{ ...fieldLabelStyle, marginBottom: "0.5rem" }}>
                            {isPcFx ? "Percentuais (por faixa de valor da NF)" : "Custos (por faixa de peso)"}
                          </div>
                          {CUSTO_KEYS.map((key) => (
                            <Chip key={key} color="#4c9dff" background="rgba(76,157,255,0.1)">
                              {getColumnLabel(currentColumnsExpected, key, { pcFx: isPcFx })}
                            </Chip>
                          ))}
                        </Col>

                        <Col md="8">
                          <div style={{ ...fieldLabelStyle, marginBottom: "0.5rem" }}>Taxas (adicionais ao frete)</div>
                          {semanticAnalysisSummary && detectedAdditionalCharges.length > 0 ? (
                            <div style={{ marginBottom: "0.65rem" }}>
                              <Chip
                                color={semanticAnalysisSummary.color}
                                background={semanticAnalysisSummary.background}
                              >
                                {semanticAnalysisSummary.label}
                              </Chip>
                              <div style={{ ...bodyTextStyle, fontSize: "0.76rem", marginTop: "0.2rem" }}>
                                {semanticAnalysisSummary.detail}
                              </div>
                            </div>
                          ) : null}

                          {unifiedTaxaEntries.length ? unifiedTaxaEntries.map((entry) => {
                            if (entry.entryType === "route") {
                              const key = entry.itemKey;
                              const info = entry.routeInfo || {};
                              const matches = info.fretaTaxaMatches || [];
                              const allTaxas = analysis.allFretaTaxas || [];
                              const effectiveConfig = effectiveTaxaConfigs.find((config) => config.columnKey === key) || null;
                              const confirmed = taxaConfirmations[key];
                              const valueOverride = taxaValueOverrides[key];
                              const valorRegraOverride = taxaValorRegraOverrides[key];
                              const suggestedValorRegra = effectiveConfig?.valorRegraSugerido;
                              const displayedValorRegra = valorRegraOverride !== undefined
                                ? valorRegraOverride
                                : suggestedValorRegra ?? "";
                              const effectiveValue = valueOverride !== undefined ? valueOverride : info.valorUnico;
                              const isUniformeEfetivo = info.isUniforme || valueOverride !== undefined;
                              const isExcluded = Boolean(effectiveConfig?.excluded);

                              return (
                                <div
                                  key={`route-${key}`}
                                  style={{
                                    marginBottom: "0.55rem",
                                    padding: "0.5rem 0.75rem",
                                    borderRadius: 8,
                                    border: `1px solid ${isExcluded ? "#6c757d" : "#2b3553"}`,
                                    background: "rgba(19,28,48,0.6)",
                                    opacity: isExcluded ? 0.55 : 1,
                                  }}
                                >
                                  <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
                                    <span style={{ color: "#dbe7ff", fontSize: "0.82rem", fontWeight: 600, minWidth: 90 }}>
                                      {entry.label}
                                    </span>

                                    {isUniformeEfetivo ? (
                                      <Chip color="#2ecc71" background="rgba(46,204,113,0.1)">
                                        Uniforme → {effectiveValue}
                                      </Chip>
                                    ) : (
                                      <Chip color="#f39c12" background="rgba(243,156,18,0.1)">
                                        Varia por rota
                                        {info.hasFalhasOcr && ` (${info.rotasComValor}/${info.totalRotas} lidas)`}
                                      </Chip>
                                    )}

                                    {isUniformeEfetivo && (
                                      <Chip color="#9b59b6" background="rgba(155,89,182,0.1)">
                                        Candidata a cadastro global
                                      </Chip>
                                    )}
                                    {isExcluded && (
                                      <Chip color="#6c757d" background="rgba(108,117,125,0.14)">
                                        Excluida do save
                                      </Chip>
                                    )}

                                    <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                                      <span style={{ color: "#8f9bc7", fontSize: "0.72rem" }}>FretaTaxa:</span>
                                      <Input
                                        type="select"
                                        bsSize="sm"
                                        disabled={isExcluded}
                                        value={confirmed !== undefined ? String(confirmed) : matches.length > 0 ? String(matches[0].id) : ""}
                                        onChange={(e) => handleTaxaConfirmation(key, Number(e.target.value))}
                                        style={{ background: "#131c30", color: "#dbe7ff", border: "1px solid #2b3553", fontSize: "0.78rem", padding: "0.15rem 0.3rem", height: "auto", minWidth: 160 }}
                                      >
                                        {matches.length === 0 && confirmed === undefined && (
                                          <option value="">— selecionar —</option>
                                        )}
                                        {allTaxas.map((m) => (
                                          <option key={m.id} value={m.id}>{m.nome} ({m.codigo})</option>
                                        ))}
                                      </Input>
                                    </div>

                                    <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                                      <span style={{ color: "#8f9bc7", fontSize: "0.72rem" }}>ValorRegra:</span>
                                      <Input
                                        type="number"
                                        bsSize="sm"
                                        step="0.0001"
                                        disabled={isExcluded}
                                        placeholder={suggestedValorRegra !== null && suggestedValorRegra !== undefined ? String(suggestedValorRegra) : "ex: 100"}
                                        value={displayedValorRegra}
                                        onChange={(e) => {
                                          const v = e.target.value;
                                          setTaxaValorRegraOverrides((prev) => ({
                                            ...prev,
                                            [key]: v === "" ? undefined : Number(v),
                                          }));
                                        }}
                                        style={{ background: "#131c30", color: "#dbe7ff", border: "1px solid #2b3553", fontSize: "0.78rem", padding: "0.15rem 0.3rem", height: "auto", minWidth: 110, width: 110 }}
                                      />
                                      {suggestedValorRegra !== null && suggestedValorRegra !== undefined && (
                                        <span style={{ color: "#8f9bc7", fontSize: "0.7rem" }}>
                                          Sugestão: {suggestedValorRegra}
                                        </span>
                                      )}
                                      {valorRegraOverride !== undefined && (
                                        <span
                                          style={{ color: "#e74c3c", fontSize: "0.72rem", cursor: "pointer" }}
                                          onClick={() => setTaxaValorRegraOverrides((prev) => {
                                            const next = { ...prev };
                                            delete next[key];
                                            return next;
                                          })}
                                        >
                                          ✕ limpar
                                        </span>
                                      )}
                                    </div>

                                    <span
                                      style={{ color: isExcluded ? "#2ecc71" : "#e74c3c", fontSize: "0.72rem", cursor: "pointer" }}
                                      onClick={() => handleToggleTaxaExclusion(key)}
                                    >
                                      {isExcluded ? "Restaurar" : "Excluir"}
                                    </span>
                                  </div>

                                  {!info.isUniforme && (
                                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.4rem" }}>
                                      <span style={{ color: "#8f9bc7", fontSize: "0.72rem" }}>Valor único manual:</span>
                                      <Input
                                        type="number"
                                        bsSize="sm"
                                        disabled={isExcluded}
                                        placeholder="ex: 0.40"
                                        value={valueOverride !== undefined ? valueOverride : ""}
                                        onChange={(e) => {
                                          const v = e.target.value;
                                          setTaxaValueOverrides((prev) => ({ ...prev, [key]: v === "" ? undefined : Number(v) }));
                                        }}
                                        style={{ background: "#131c30", color: "#dbe7ff", border: "1px solid #2b3553", fontSize: "0.78rem", width: 100, padding: "0.15rem 0.3rem", height: "auto" }}
                                      />
                                      {valueOverride !== undefined && (
                                        <span
                                          style={{ color: "#e74c3c", fontSize: "0.72rem", cursor: "pointer" }}
                                          onClick={() => setTaxaValueOverrides((prev) => {
                                            const next = { ...prev };
                                            delete next[key];
                                            return next;
                                          })}
                                        >
                                          ✕ limpar
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            }

                            const config = entry.config;
                            const sourceMeta = getAdditionalChargeSourceMeta(config.source);
                            const allTaxas = analysis.allFretaTaxas || [];
                            const confirmed = additionalChargeConfirmations[config.itemKey];
                            const valueOverride = additionalChargeValueOverrides[config.itemKey];
                            const valorRegraOverride = additionalChargeValorRegraOverrides[config.itemKey];
                            const displayedValorRegra = valorRegraOverride !== undefined
                              ? valorRegraOverride
                              : config.valorRegraSugerido ?? "";
                            const effectiveValue = valueOverride !== undefined
                              ? valueOverride
                              : (config.rawValue || config.parsedValue || "");

                            return (
                              <div
                                key={`additional-${config.itemKey}`}
                                style={{
                                  marginBottom: "0.55rem",
                                  padding: "0.5rem 0.75rem",
                                  borderRadius: 8,
                                  border: `1px solid ${config.excluded ? "#6c757d" : "#2b3553"}`,
                                  background: "rgba(19,28,48,0.6)",
                                  opacity: config.excluded ? 0.55 : 1,
                                }}
                              >
                                <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
                                  <span style={{ color: "#dbe7ff", fontSize: "0.82rem", fontWeight: 600, minWidth: 150 }}>
                                    {config.label}
                                  </span>

                                  {effectiveValue !== "" && effectiveValue !== null && effectiveValue !== undefined && (
                                    <Chip color="#2ecc71" background="rgba(46,204,113,0.1)">
                                      Global → {effectiveValue}
                                    </Chip>
                                  )}

                                  <Chip color="#9b59b6" background="rgba(155,89,182,0.1)">
                                    Generalidade global
                                  </Chip>
                                  <Chip color={sourceMeta.color} background={sourceMeta.background}>
                                    {sourceMeta.label}
                                  </Chip>
                                  {config.interpolationGroup === "fracao_100kg" && (
                                    <Chip color="#f39c12" background="rgba(243,156,18,0.1)">
                                      Por fracao de 100kg
                                    </Chip>
                                  )}
                                  {config.excluded && (
                                    <Chip color="#6c757d" background="rgba(108,117,125,0.14)">
                                      Excluida do save
                                    </Chip>
                                  )}

                                  <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                                    <span style={{ color: "#8f9bc7", fontSize: "0.72rem" }}>FretaTaxa:</span>
                                    <Input
                                      type="select"
                                      bsSize="sm"
                                      disabled={config.excluded}
                                      value={confirmed !== undefined ? String(confirmed) : config.matches.length > 0 ? String(config.matches[0].id) : ""}
                                      onChange={(e) => handleAdditionalChargeConfirmation(config.itemKey, Number(e.target.value))}
                                      style={{ background: "#131c30", color: "#dbe7ff", border: "1px solid #2b3553", fontSize: "0.78rem", padding: "0.15rem 0.3rem", height: "auto", minWidth: 180 }}
                                    >
                                      {config.matches.length === 0 && confirmed === undefined && (
                                        <option value="">— selecionar —</option>
                                      )}
                                      {allTaxas.map((m) => (
                                        <option key={m.id} value={m.id}>{m.nome} ({m.codigo})</option>
                                      ))}
                                    </Input>
                                  </div>

                                  <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                                    <span style={{ color: "#8f9bc7", fontSize: "0.72rem" }}>ValorRegra:</span>
                                    <Input
                                      type="number"
                                      bsSize="sm"
                                      step="0.0001"
                                      disabled={config.excluded}
                                      placeholder={config.valorRegraSugerido !== null && config.valorRegraSugerido !== undefined ? String(config.valorRegraSugerido) : "ex: 0"}
                                      value={displayedValorRegra}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        setAdditionalChargeValorRegraOverrides((prev) => ({
                                          ...prev,
                                          [config.itemKey]: v === "" ? undefined : Number(v),
                                        }));
                                      }}
                                      style={{ background: "#131c30", color: "#dbe7ff", border: "1px solid #2b3553", fontSize: "0.78rem", padding: "0.15rem 0.3rem", height: "auto", minWidth: 110, width: 110 }}
                                    />
                                    {config.valorRegraSugerido !== null && config.valorRegraSugerido !== undefined && (
                                      <span style={{ color: "#8f9bc7", fontSize: "0.7rem" }}>
                                        Sugestão: {config.valorRegraSugerido}
                                      </span>
                                    )}
                                  </div>

                                  <span
                                    style={{ color: config.excluded ? "#2ecc71" : "#e74c3c", fontSize: "0.72rem", cursor: "pointer" }}
                                    onClick={() => handleToggleAdditionalChargeExclusion(config.itemKey)}
                                  >
                                    {config.excluded ? "Restaurar" : "Excluir"}
                                  </span>
                                </div>

                                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.4rem", flexWrap: "wrap" }}>
                                  <span style={{ color: "#8f9bc7", fontSize: "0.72rem" }}>Valor global manual:</span>
                                  <Input
                                    type="number"
                                    bsSize="sm"
                                    disabled={config.excluded}
                                    placeholder={config.rawValue || "ex: 18.90"}
                                    value={valueOverride !== undefined ? valueOverride : ""}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      setAdditionalChargeValueOverrides((prev) => ({
                                        ...prev,
                                        [config.itemKey]: v === "" ? undefined : Number(v),
                                      }));
                                    }}
                                    style={{ background: "#131c30", color: "#dbe7ff", border: "1px solid #2b3553", fontSize: "0.78rem", width: 110, padding: "0.15rem 0.3rem", height: "auto" }}
                                  />
                                  {valueOverride !== undefined && (
                                    <span
                                      style={{ color: "#e74c3c", fontSize: "0.72rem", cursor: "pointer" }}
                                      onClick={() => setAdditionalChargeValueOverrides((prev) => {
                                        const next = { ...prev };
                                        delete next[config.itemKey];
                                        return next;
                                      })}
                                    >
                                      ✕ limpar
                                    </span>
                                  )}
                                </div>

                                {config.sourceLine && (
                                  <div style={{ color: "#8f9bc7", fontSize: "0.74rem", lineHeight: 1.45, marginTop: "0.3rem" }}>
                                    {config.sourceLine}
                                  </div>
                                )}
                              </div>
                            );
                          }) : (
                            <div style={bodyTextStyle}>Nenhuma taxa extraida.</div>
                          )}
                        </Col>
                      </Row>
                    </div>
                  </Col>
                </Row>
              )}

              <Row>
                <Col lg="5" style={{ marginBottom: "1rem" }}>
                  <div style={panelStyle}>
                    <div style={sectionTitleStyle}>Cobertura de Capitais</div>
                    {Array.isArray(analysis?.draft?.coberturasCapital) && analysis.draft.coberturasCapital.length ? (
                      analysis.draft.coberturasCapital.map((item, index) => (
                        <div
                          key={`${item.uf || "uf"}-${index}`}
                          style={{
                            border: "1px solid #2b3553",
                            borderRadius: 8,
                            padding: "0.7rem 0.8rem",
                            marginBottom: "0.6rem",
                          }}
                        >
                          <div style={{ color: "#dbe7ff", fontWeight: 600, fontSize: "0.83rem" }}>
                            {item.uf || "UF nao identificada"}
                          </div>
                          <div style={{ color: "#9fb0da", fontSize: "0.79rem", marginTop: "0.2rem", lineHeight: 1.45 }}>
                            {item.cidades}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={bodyTextStyle}>Nenhuma cobertura de capital detectada.</div>
                    )}
                  </div>
                </Col>

                <Col lg="7" style={{ marginBottom: "1rem" }}>
                  <div style={panelStyle}>
                    <div style={sectionTitleStyle}>Observacoes Principais</div>
                    {Array.isArray(analysis?.draft?.observacoes) && analysis.draft.observacoes.length ? (
                      analysis.draft.observacoes.map((item) => (
                        <div
                          key={item.ordem}
                          style={{
                            border: "1px solid #2b3553",
                            borderRadius: 8,
                            padding: "0.75rem 0.85rem",
                            marginBottom: "0.6rem",
                          }}
                        >
                          <div style={{ color: "#8f9bc7", fontSize: "0.72rem", marginBottom: "0.25rem" }}>
                            Bloco {item.ordem}
                          </div>
                          <div style={{ color: "#dbe7ff", fontSize: "0.81rem", lineHeight: 1.55 }}>
                            {item.texto}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={bodyTextStyle}>Sem observacoes estruturadas.</div>
                    )}
                  </div>
                </Col>
              </Row>


              <Row>
                <Col lg="12" style={{ marginBottom: "1rem" }}>
                  <div style={panelStyle}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap", marginBottom: "0.8rem" }}>
                      <div>
                        <div style={sectionTitleStyle}>Linhas Normalizadas Para o Flow</div>
                        <div style={{ color: "#8f9bc7", fontSize: "0.8rem" }}>
                          Esta e a transformacao principal do teste: as colunas da tabela viram linhas estruturadas.
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                        <Chip color="#2ecc71" background="rgba(46,204,113,0.12)">
                          Rotas: {structuredJsonPreview?.tableModel?.routeCount || 0}
                        </Chip>
                        <Chip color="#4c9dff" background="rgba(76,157,255,0.12)">
                          Linhas: {structuredJsonPreview?.tableModel?.flowLineCount || 0}
                        </Chip>
                      </div>
                    </div>

                    {Array.isArray(structuredJsonPreview?.tableModel?.flowLines) &&
                    structuredJsonPreview.tableModel.flowLines.length ? (
                      <div className="table-responsive">
                        <Table dark style={{ marginBottom: 0 }}>
                          <thead>
                            <tr>
                              <th>Rota</th>
                              <th>Campo</th>
                              <th>Secao</th>
                              <th>Valor bruto</th>
                              <th>Valor lido</th>
                              <th>Interpolacao</th>
                            </tr>
                          </thead>
                          <tbody>
                            {structuredJsonPreview.tableModel.flowLines.slice(0, 24).map((item, index) => (
                              <tr key={`${item.routeKey}-${item.columnKey}-${index}`}>
                                <td>{item.routeLabel}</td>
                                <td>{item.columnLabel}</td>
                                <td>{item.section}</td>
                                <td>{item.rawValue}</td>
                                <td>{item.parsedValue ?? "-"}</td>
                                <td>{item.interpolationGroup || "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      <div style={bodyTextStyle}>Nenhuma linha normalizada disponivel.</div>
                    )}
                  </div>
                </Col>
              </Row>

	              {/* Metadados da tabela usados apenas no JSON estruturado */}
	              {editableRoutes.length > 0 && (
	                <Row>
	                  <Col lg="12" style={{ marginBottom: "1rem" }}>
	                    <div style={panelStyle}>
	                      <div style={{ ...sectionTitleStyle, marginBottom: "0.9rem" }}>
	                        Metadados da Tabela no JSON
	                      </div>
	                      <div style={{ color: "#8f9bc7", fontSize: "0.8rem", marginBottom: "0.85rem" }}>
	                        Esta etapa nao grava nada no banco. Esses campos entram apenas no `JSON estruturado` final para validacao do fluxo.
	                      </div>

	                      <Row>
	                        <Col md="4" style={{ marginBottom: "0.6rem" }}>
	                          <FormGroup style={{ marginBottom: 0 }}>
	                            <label style={fieldLabelStyle}>Nome da tabela</label>
                            <Input
                              type="text"
                              placeholder="ex: Tabela Farias 2025"
                              value={saveForm.nome}
                              onChange={(e) => handleFieldChange("tituloTabela", e.target.value)}
                              style={compactInputStyle}
                            />
                          </FormGroup>
                        </Col>

	                        <Col md="2" style={{ marginBottom: "0.6rem" }}>
	                          <FormGroup style={{ marginBottom: 0 }}>
	                            <label style={fieldLabelStyle}>Unidade</label>
                            <Input
                              type="select"
                              value={saveForm.idFreteUnidade}
                              onChange={(e) => { const v = e.target.value; setSaveForm((f) => ({ ...f, idFreteUnidade: v })); }}
                              style={compactInputStyle}
                            >
                              <option value="">— selecionar —</option>
                              {freteUnidades.map((u) => (
                                <option key={u.id} value={u.id}>{u.codigo} — {u.nome}</option>
                              ))}
                            </Input>
                          </FormGroup>
                        </Col>

	                        <Col md="2" style={{ marginBottom: "0.6rem" }}>
	                          <FormGroup style={{ marginBottom: 0 }}>
	                            <label style={fieldLabelStyle}>Modal</label>
                            <Input
                              type="select"
                              value={saveForm.idModal}
                              onChange={(e) => { const v = e.target.value; setSaveForm((f) => ({ ...f, idModal: v })); }}
                              style={compactInputStyle}
                            >
                              <option value="">— selecionar —</option>
                              {modais.map((m) => (
                                <option key={m.id} value={m.id}>{m.nome}</option>
                              ))}
                            </Input>
                          </FormGroup>
                        </Col>

	                        <Col md="4" style={{ marginBottom: "0.6rem" }}>
	                          <FormGroup style={{ marginBottom: 0 }}>
	                            <label style={fieldLabelStyle}>Negócio</label>
                            <Input
                              type="select"
                              value={saveForm.idNegocio}
                              onChange={(e) => { const v = e.target.value; setSaveForm((f) => ({ ...f, idNegocio: v })); }}
                              style={compactInputStyle}
                            >
                              <option value="">— selecionar —</option>
                              {negocios.map((n) => (
                                <option key={n.id} value={n.id}>{n.descricao}</option>
                              ))}
                            </Input>
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row>
                        <Col md="3" style={{ marginBottom: "0.6rem" }}>
                          <FormGroup style={{ marginBottom: 0 }}>
                            <label style={fieldLabelStyle}>Tipo de Serviço</label>
                            <Input
                              type="select"
                              value={saveForm.idTipoServico}
                              onChange={(e) => { const v = e.target.value; setSaveForm((f) => ({ ...f, idTipoServico: v })); }}
                              style={compactInputStyle}
                            >
                              <option value="">— selecionar —</option>
                              {tiposServico.map((t) => (
                                <option key={t.id} value={t.id}>{t.nome}</option>
                              ))}
                            </Input>
                          </FormGroup>
                        </Col>

                        <Col md="3" style={{ marginBottom: "0.6rem" }}>
                          <FormGroup style={{ marginBottom: 0 }}>
                            <label style={fieldLabelStyle}>Tipo de Frete</label>
                            <Input
                              type="select"
                              value={saveForm.idTipoFrete}
                              onChange={(e) => { const v = e.target.value; setSaveForm((f) => ({ ...f, idTipoFrete: v })); }}
                              style={compactInputStyle}
                            >
                              <option value="">— selecionar —</option>
                              {tiposFrete.map((t) => (
                                <option key={t.id} value={t.id}>{t.nome}</option>
                              ))}
                            </Input>
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row>
                        <Col md="4" style={{ marginBottom: "0.6rem" }}>
                          <FormGroup style={{ marginBottom: 0 }}>
                            <label style={fieldLabelStyle}>Origem cidade</label>
                            <Input
                              type="text"
                              value={saveForm.origemCidade}
                              readOnly
                              style={compactInputStyle}
                            />
                          </FormGroup>
                        </Col>

                        <Col md="2" style={{ marginBottom: "0.6rem" }}>
                          <FormGroup style={{ marginBottom: 0 }}>
                            <label style={fieldLabelStyle}>Origem UF</label>
                            <Input
                              type="text"
                              value={saveForm.origemUf}
                              readOnly
                              style={compactInputStyle}
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row>
                        <Col md="2" style={{ marginBottom: "0.6rem" }}>
                          <FormGroup style={{ marginBottom: 0 }}>
                            <label style={fieldLabelStyle}>Vigência início</label>
                            <Input
                              type="date"
                              value={saveForm.dataValIni}
                              readOnly
                              style={compactInputStyle}
                            />
                          </FormGroup>
                        </Col>

                        <Col md="2" style={{ marginBottom: "0.6rem" }}>
                          <FormGroup style={{ marginBottom: 0 }}>
                            <label style={fieldLabelStyle}>Vigência fim</label>
                            <Input
                              type="date"
                              value={saveForm.dataValFim}
                              readOnly
                              style={compactInputStyle}
                            />
                          </FormGroup>
                          </Col>
                        </Row>
                      </div>
                    </Col>
                  </Row>
                )}

              {editableRoutes.length > 0 && (
                <Row>
                  <Col lg="12" style={{ marginBottom: "1rem" }}>
                    <div style={panelStyle}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap", marginBottom: "0.8rem" }}>
                        <div>
                          <div style={sectionTitleStyle}>Ajuste de Rotas</div>
                          <div style={{ color: "#8f9bc7", fontSize: "0.8rem" }}>
                            Revise UF, rota e valores extraidos antes de salvar. Celulas marcadas com ⚠ podem ter erro de OCR.
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                          <Chip color="#f39c12" background="rgba(243,156,18,0.12)">
                            {editableRoutes.length} rotas
                          </Chip>
                          <Button color="success" size="sm" onClick={handleAddRoute}>
                            + Adicionar Rota
                          </Button>
                        </div>
                      </div>
                      <div className="table-responsive">
                        <Table dark style={{ marginBottom: 0, fontSize: "0.78rem" }}>
                          <thead>
                            <tr>
                              <th style={{ minWidth: 48 }}>UF</th>
                              <th style={{ minWidth: 120 }}>Rota</th>
                              {ROUTE_ADJUST_KEYS.map((key) => (
                                <th key={key} style={{ minWidth: 70, whiteSpace: "nowrap" }}>
                                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                    <span>{CUSTO_KEYS.includes(key) ? getColumnLabel(currentColumnsExpected, key, { pcFx: isPcFx }) : COL_SHORT[key]}</span>
                                    {CUSTO_KEYS.includes(key) && (
                                      <>
                                        <div style={{ display: "flex", gap: 4 }}>
                                          {COLUMN_CALCULATION_MODES.map((mode) => {
                                            // PO1-3916: PcFx travado em Fixo.
                                            const modoAtual = isPcFx ? "fixed" : (columnCalculationModes[key] || "fixed");
                                            const active = modoAtual === mode;
                                            const bloqueado = isPcFx && mode !== "fixed";
                                            return (
                                              <button
                                                key={`${key}-${mode}`}
                                                type="button"
                                                disabled={bloqueado}
                                                title={bloqueado ? "Percentual sobre a NF: só o modo Fixo" : undefined}
                                                onClick={() => handleColumnCalculationModeChange(key, mode)}
                                                style={{
                                                  background: active ? "rgba(29, 140, 248, 0.18)" : "#131c30",
                                                  border: `1px solid ${active ? "#1d8cf8" : "#2b3553"}`,
                                                  color: active ? "#f5f7fb" : "#8f9bc7",
                                                  borderRadius: 6,
                                                  padding: "0.12rem 0.35rem",
                                                  fontSize: "0.64rem",
                                                  lineHeight: 1.2,
                                                  cursor: bloqueado ? "not-allowed" : "pointer",
                                                  opacity: bloqueado ? 0.45 : 1,
                                                }}
                                              >
                                                {COLUMN_CALCULATION_MODE_LABELS[mode]}
                                              </button>
                                            );
                                          })}
                                        </div>
                                        {!isPcFx && (columnCalculationModes[key] || "fixed") === "per_unit_total" && (
                                          <input
                                            type="number"
                                            min="0.01"
                                            step="any"
                                            title="Divisor: 1 = por kg, 100 = por 100 kg, 1000 = por tonelada (Custo ÷ divisor × peso total)"
                                            value={columnDivisors[key] !== undefined ? columnDivisors[key] : DEFAULT_COLUMN_DIVISOR}
                                            onChange={(event) => handleColumnDivisorChange(key, event.target.value)}
                                            style={{ width: 70, background: "#131c30", border: "1px solid #2b3553", color: "#f5f7fb", borderRadius: 6, padding: "0.1rem 0.3rem", fontSize: "0.64rem" }}
                                          />
                                        )}
                                        {detectedColumnCalculationMetadata[key]?.detectedByOcr && (
                                          <span style={{ color: "#2ecc71", fontSize: "0.62rem", lineHeight: 1.2 }}>
                                            OCR: excedente
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </th>
                              ))}
                              <th style={{ width: 32 }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {editableRoutes.map((route) => (
                              <tr key={route.routeIndex}>
                                <td style={{ padding: "0.25rem 0.4rem" }}>
                                  <input
                                    value={route.ufDestino}
                                    maxLength={2}
                                    onChange={(e) => handleRouteUfChange(route.routeIndex, e.target.value)}
                                    style={{
                                      width: 40,
                                      background: VALID_UFS.has(route.ufDestino) ? "#131c30" : "rgba(231,76,60,0.18)",
                                      border: "1px solid " + (VALID_UFS.has(route.ufDestino) ? "#2b3553" : "#e74c3c"),
                                      color: "#dbe7ff",
                                      borderRadius: 4,
                                      padding: "0.2rem 0.3rem",
                                      fontSize: "0.78rem",
                                      textTransform: "uppercase",
                                      textAlign: "center",
                                    }}
                                  />
                                </td>
                                <td style={{ padding: "0.25rem 0.4rem" }}>
                                  <input
                                    value={route.routeLabel}
                                    onChange={(e) => handleRouteLabelChange(route.routeIndex, e.target.value)}
                                    style={{
                                      width: "100%",
                                      minWidth: 110,
                                      background: "#131c30",
                                      border: "1px solid #2b3553",
                                      color: "#dbe7ff",
                                      borderRadius: 4,
                                      padding: "0.2rem 0.35rem",
                                      fontSize: "0.78rem",
                                    }}
                                  />
                                </td>
                                {ROUTE_ADJUST_KEYS.map((key) => {
                                  const suspicious = isSuspiciousValue(key, route.values[key], editableRoutes);
                                  return (
                                    <td key={key} style={{ padding: "0.25rem 0.4rem" }}>
                                      <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                                        <input
                                          value={route.values[key] !== undefined ? route.values[key] : ""}
                                          onChange={(e) => handleRouteValueChange(route.routeIndex, key, e.target.value)}
                                          style={{
                                            width: 62,
                                            background: suspicious ? "rgba(231,76,60,0.14)" : "#131c30",
                                            border: "1px solid " + (suspicious ? "#e74c3c" : "#2b3553"),
                                            color: suspicious ? "#f7a8a8" : "#dbe7ff",
                                            borderRadius: 4,
                                            padding: "0.2rem 0.3rem",
                                            fontSize: "0.78rem",
                                            textAlign: "right",
                                          }}
                                        />
                                        {suspicious && (
                                          <span title="Valor suspeito — verifique o OCR" style={{ color: "#f39c12", fontSize: "0.82rem" }}>
                                            ⚠
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                  );
                                })}
                                <td style={{ padding: "0.25rem 0.3rem", textAlign: "center" }}>
                                  <button
                                    onClick={() => handleRemoveRoute(route.routeIndex)}
                                    title="Remover rota"
                                    style={{
                                      background: "transparent",
                                      border: "none",
                                      color: "#e74c3c",
                                      cursor: "pointer",
                                      fontSize: "0.9rem",
                                      padding: "0.1rem 0.3rem",
                                      lineHeight: 1,
                                    }}
                                  >
                                    ✕
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  </Col>
                </Row>
              )}

              <Row>
                <Col lg="8" style={{ marginBottom: "1rem" }}>
                  <div style={panelStyle}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap", marginBottom: "0.8rem" }}>
                      <div>
                        <div style={sectionTitleStyle}>Revisao Capital vs Interior</div>
                        <div style={{ color: "#8f9bc7", fontSize: "0.8rem" }}>
                          Depois do ajuste de rotas, defina quais cidades entram como capital por UF. O restante da UF entra automaticamente como interior no JSON.
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                        <Chip color="#4c9dff" background="rgba(76,157,255,0.12)">
                          UFs revisadas: {capitalReviewData.length}
                        </Chip>
                        <Chip color="#2ecc71" background="rgba(46,204,113,0.12)">
                          Linhas resolvidas: {freteTabelaCustoPreview.summary.resolvedLineCount}
                        </Chip>
                      </div>
                    </div>

                    {capitalReviewData.length === 0 ? (
                      <div style={bodyTextStyle}>Nenhuma UF com divisao capital/interior foi identificada no rascunho atual.</div>
                    ) : (
                      capitalReviewData.map((item) => (
                        <div
                          key={item.uf}
                          style={{
                            border: "1px solid #2b3553",
                            borderRadius: 8,
                            padding: "0.85rem 0.9rem",
                            marginBottom: "0.75rem",
                            background: "rgba(19,28,48,0.45)",
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", gap: "0.75rem", flexWrap: "wrap", marginBottom: "0.65rem" }}>
                            <div>
                              <div style={{ color: "#dbe7ff", fontWeight: 600, fontSize: "0.84rem" }}>
                                {item.uf}
                              </div>
                              <div style={{ color: "#8f9bc7", fontSize: "0.77rem" }}>
                                Cidades carregadas: {item.cityCount} | Capital selecionada: {item.selectedCapitalCities.length} | Interior: {item.interiorCities.length}
                              </div>
                            </div>
                            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                              <Button color="secondary" size="sm" outline onClick={() => handleApplyCapitalFallback(item.uf)}>
                                Reaplicar OCR/fallback
                              </Button>
                              <Button color="secondary" size="sm" outline onClick={() => handleClearCapitalSelection(item.uf)}>
                                Limpar capital
                              </Button>
                            </div>
                          </div>

                          <div style={{ marginBottom: "0.6rem" }}>
                            <div style={fieldLabelStyle}>Detectado no OCR</div>
                            {item.detectedNames.length > 0 ? (
                              item.detectedNames.map((name) => (
                                <Chip key={`${item.uf}-${name}`} color="#f39c12" background="rgba(243,156,18,0.1)">
                                  {name}
                                </Chip>
                              ))
                            ) : (
                              <div style={bodyTextStyle}>Sem cidades detectadas diretamente no texto OCR.</div>
                            )}
                          </div>

                          {item.selectedCapitalCities.length > 0 && (
                            <div style={{ marginBottom: "0.7rem" }}>
                              <div style={fieldLabelStyle}>Capitais selecionadas</div>
                              {item.selectedCapitalCities.map((city) => (
                                <Chip key={`selected-${item.uf}-${city.id}`} color="#2ecc71" background="rgba(46,204,113,0.12)">
                                  {city.nome}
                                </Chip>
                              ))}
                            </div>
                          )}

                          {!item.loadingCities && item.cityCount > 0 && (
                            <div style={{ marginBottom: "0.7rem" }}>
                              <div style={fieldLabelStyle}>Buscar cidade</div>
                              <Input
                                type="text"
                                bsSize="sm"
                                value={citySearchByUf[item.uf] || ""}
                                onChange={(event) => handleCitySearchChange(item.uf, event.target.value)}
                                placeholder="Digite para filtrar por nome"
                                style={{
                                  background: "#131c30",
                                  borderColor: "#2b3553",
                                  color: "#dbe7ff",
                                  fontSize: "0.8rem",
                                }}
                              />
                            </div>
                          )}

                          {item.loadingCities ? (
                            <div style={{ color: "#8f9bc7", fontSize: "0.8rem" }}>
                              <Spinner size="sm" /> Carregando cidades da UF...
                            </div>
                          ) : item.cityCount === 0 ? (
                            <div style={bodyTextStyle}>Nao foi possivel carregar o catalogo de cidades desta UF.</div>
                          ) : (
                            <div
                              style={{
                                maxHeight: 220,
                                overflowY: "auto",
                                paddingRight: "0.35rem",
                                borderTop: "1px solid #2b3553",
                                paddingTop: "0.65rem",
                              }}
                            >
                              {Array.isArray(cidadesByUf[item.uf]) && cidadesByUf[item.uf]
                                .filter((city) => {
                                  const term = normalizeSearch(citySearchByUf[item.uf] || "");
                                  const checked = (capitalSelectionsByUf[item.uf] || []).includes(String(city.id));
                                  if (checked) {
                                    return true;
                                  }
                                  if (!term) {
                                    return true;
                                  }

                                  return normalizeSearch(city.nome).includes(term);
                                })
                                .map((city) => {
                                  const checked = (capitalSelectionsByUf[item.uf] || []).includes(String(city.id));
                                  return (
                                    <label
                                      key={`${item.uf}-${city.id}`}
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: "0.75rem",
                                        padding: "0.45rem 0",
                                        borderBottom: "1px solid rgba(43,53,83,0.45)",
                                        color: "#dbe7ff",
                                        fontSize: "0.8rem",
                                        marginBottom: 0,
                                        cursor: "pointer",
                                      }}
                                    >
                                      <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                        <span
                                          style={{
                                            width: 18,
                                            height: 18,
                                            borderRadius: 5,
                                            border: checked ? "1px solid #2ecc71" : "1px solid #4b577f",
                                            background: checked ? "rgba(46,204,113,0.18)" : "transparent",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: checked ? "#2ecc71" : "transparent",
                                            fontSize: "0.72rem",
                                            fontWeight: 700,
                                            flexShrink: 0,
                                            boxShadow: checked ? "0 0 0 1px rgba(46,204,113,0.18)" : "none",
                                          }}
                                        >
                                          ✓
                                        </span>
                                        <input
                                          type="checkbox"
                                          checked={checked}
                                          onChange={(event) => handleCapitalSelectionChange(item.uf, city.id, event.target.checked)}
                                          style={{ display: "none" }}
                                        />
                                        <span>{city.nome}</span>
                                      </span>
                                      <span style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap", justifyContent: "flex-end" }}>
                                        {city.isCapital && (
                                          <Chip color="#2ecc71" background="rgba(46,204,113,0.1)">
                                            Capital UF
                                          </Chip>
                                        )}
                                        {city.cepInicial && (
                                          <Chip color="#4c9dff" background="rgba(76,157,255,0.1)">
                                            CEP {city.cepInicial}{city.cepFinal && city.cepFinal !== city.cepInicial ? `-${city.cepFinal}` : ""}
                                          </Chip>
                                        )}
                                      </span>
                                    </label>
                                  );
                                })}
                              {Array.isArray(cidadesByUf[item.uf]) && cidadesByUf[item.uf].filter((city) => {
                                const term = normalizeSearch(citySearchByUf[item.uf] || "");
                                if (!term) {
                                  return true;
                                }

                                return normalizeSearch(city.nome).includes(term);
                              }).length === 0 && (
                                <div style={bodyTextStyle}>Nenhuma cidade encontrada para esse filtro.</div>
                              )}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </Col>

                <Col lg="4" style={{ marginBottom: "1rem" }}>
                  <div style={panelStyle}>
                    <div style={sectionTitleStyle}>Sugestoes de Agrupamento</div>
                    {groupingSuggestions.length > 0 ? (
                      groupingSuggestions.map((item) => (
                        <div
                          key={`group-${item.uf}`}
                          style={{
                            border: "1px solid #2b3553",
                            borderRadius: 8,
                            padding: "0.7rem 0.8rem",
                            marginBottom: "0.6rem",
                          }}
                        >
                          <div style={{ color: "#dbe7ff", fontWeight: 600, fontSize: "0.83rem" }}>
                            {item.uf}
                          </div>
                          <div style={{ color: "#9fb0da", fontSize: "0.79rem", marginTop: "0.25rem", lineHeight: 1.45 }}>
                            {item.sourceRoutes.join(" | ")}
                          </div>
                          <div style={{ marginTop: "0.45rem" }}>
                            <Chip
                              color={item.canGroup ? "#2ecc71" : "#f39c12"}
                              background={item.canGroup ? "rgba(46,204,113,0.1)" : "rgba(243,156,18,0.1)"}
                            >
                              {item.canGroup ? "Candidata a agrupamento" : "Valores distintos"}
                            </Chip>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={bodyTextStyle}>Sem sugestoes de agrupamento para o conjunto atual de rotas.</div>
                    )}

                    {freteTabelaCustoPreview.pendingGroups.length > 0 && (
                      <div style={{ marginTop: "1rem" }}>
                        <div style={fieldLabelStyle}>Pendencias da previa</div>
                        {freteTabelaCustoPreview.pendingGroups.map((item) => (
                          <div
                            key={`${item.uf}-${item.reason}`}
                            style={{
                              border: "1px dashed #f39c12",
                              borderRadius: 8,
                              padding: "0.65rem 0.75rem",
                              color: "#ffd28a",
                              fontSize: "0.78rem",
                              marginBottom: "0.5rem",
                            }}
                          >
                            <strong>{item.uf}</strong>: {item.message}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Col>
              </Row>

              <Row>
                <Col lg="12" style={{ marginBottom: "1rem" }}>
                  <div style={panelStyle}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap", marginBottom: "0.8rem" }}>
                      <div>
                        <div style={sectionTitleStyle}>Previa de FreteTabelaCusto</div>
                        <div style={{ color: "#8f9bc7", fontSize: "0.8rem" }}>
                          Esta previa nao persiste em banco. Ela existe apenas para validar o JSON estruturado final.
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                        <Chip color="#2ecc71" background="rgba(46,204,113,0.12)">
                          Resolvidas: {freteTabelaCustoPreview.summary.resolvedLineCount}
                        </Chip>
                        <Chip color="#f39c12" background="rgba(243,156,18,0.12)">
                          Pendencias: {freteTabelaCustoPreview.summary.pendingGroupCount}
                        </Chip>
                        {resolvedLinePreparation.dedupedCount > 0 && (
                          <Chip color="#4c9dff" background="rgba(76,157,255,0.12)">
                            Duplicatas equivalentes: {resolvedLinePreparation.dedupedCount}
                          </Chip>
                        )}
                        {resolvedLinePreparation.conflicts.length > 0 && (
                          <Chip color="#ff6b6b" background="rgba(255,107,107,0.12)">
                            Conflitos: {resolvedLinePreparation.conflicts.length}
                          </Chip>
                        )}
                      </div>
                    </div>

                    {resolvedLinePreparation.rows.length > 0 ? (
                      <div className="table-responsive">
                        <Table dark style={{ marginBottom: 0, fontSize: "0.78rem" }}>
                          <thead>
                            <tr>
                              <th>UF</th>
                              <th>Cobertura</th>
                              <th>Cidade</th>
                              <th>CEP</th>
                              <th>Faixas</th>
                            </tr>
                          </thead>
                          <tbody>
                            {resolvedLinePreparation.rows.slice(0, 24).map((item, index) => (
                              <tr key={`${item.ufDestino}-${item.idCidadeDestino}-${index}`}>
                                <td>{item.ufDestino}</td>
                                <td>{item.tipoCobertura}</td>
                                <td>{item.cidadeDestino}</td>
                                <td>
                                  {item.cepInicialDestino || "-"}
                                  {item.cepFinalDestino && item.cepFinalDestino !== item.cepInicialDestino ? ` - ${item.cepFinalDestino}` : ""}
                                </td>
                                <td>
                                  {item.faixasPeso.map((faixa) => `${faixa.columnLabel}: ${faixa.custo ?? "-"}`).join(" | ")}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      <div style={bodyTextStyle}>Nenhuma linha resolvida foi gerada ainda para a previa.</div>
                    )}
                    {resolvedLinePreparation.conflicts.length > 0 && (
                      <div style={{ marginTop: "0.75rem", color: "#ff9b9b", fontSize: "0.78rem", lineHeight: 1.5 }}>
                        Existem cidades atribuídas a mais de uma rota com valores diferentes. O save será bloqueado até essa UF ser ajustada.
                      </div>
                    )}
                  </div>
                </Col>
              </Row>

              {editableRoutes.length > 0 && (
                <Row>
                  <Col lg="12" style={{ marginBottom: "1rem" }}>
                    <div style={panelStyle}>
                      <div style={sectionTitleStyle}>Importar Prazos do Excel</div>
                      <div style={{ color: "#8f9bc7", fontSize: "0.8rem", marginBottom: "0.75rem" }}>
                        Envie uma planilha .xlsx com a aba de prazos. O sistema tenta encontrar a aba e as colunas de
                        <strong> UF destino</strong>, <strong>cidade destino</strong>, <strong>rota/regiao</strong> e <strong>prazo</strong>.
                        Se o layout vier diferente, ajuste o de/para manualmente e aplique na revisao de rotas.
                      </div>
                      <div style={{ display: "flex", gap: "0.6rem", alignItems: "center", flexWrap: "wrap" }}>
                        <label
                          htmlFor="prazo-excel-input"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "0.35rem 0.75rem",
                            background: "#1e2d50",
                            border: "1px solid #2b3553",
                            borderRadius: 6,
                            color: "#b9c8f2",
                            fontSize: "0.8rem",
                            cursor: "pointer",
                            gap: "0.4rem",
                          }}
                        >
                          <i className="tim-icons icon-cloud-upload-94" />
                          {prazoExcelFile ? prazoExcelFile.name : "Selecionar planilha"}
                          <input
                            id="prazo-excel-input"
                            type="file"
                            accept=".xlsx,.xls"
                            style={{ display: "none" }}
                            onChange={(e) => {
                              setPrazoExcelFile(e.target.files[0] || null);
                              setPrazoImportPreview(null);
                              setPrazoImportMapping(createEmptyPrazoImportMapping());
                              setPrazoImportedAssignments([]);
                              setPrazoImportResult(null);
                            }}
                          />
                        </label>
                        <Button
                          color="warning"
                          size="sm"
                          onClick={() => handlePreviewPrazoExcel()}
                          disabled={!prazoExcelFile || loadingPrazoExcel}
                        >
                          {loadingPrazoExcel ? <Spinner size="sm" /> : (prazoWorkbook ? "Atualizar leitura" : "Ler planilha")}
                        </Button>
                        <Button
                          color="success"
                          size="sm"
                          onClick={handleApplyPrazoExcel}
                          disabled={!prazoExcelFile || !prazoWorkbook || loadingPrazoExcel}
                        >
                          {loadingPrazoExcel ? <Spinner size="sm" /> : "Aplicar na revisao"}
                        </Button>
                        {prazoImportResult && (
                          <Chip color="#2ecc71" background="rgba(46,204,113,0.12)">
                            {prazoImportResult.matchedRows || 0} cidades vinculadas
                          </Chip>
                        )}
                        {prazoPreviewSummary && (
                          <Chip color="#4c9dff" background="rgba(76,157,255,0.12)">
                            Preview: {prazoPreviewSummary.matchedRows || 0} ok / {prazoPreviewSummary.unmatchedRows || 0} pendentes
                          </Chip>
                        )}
                        {prazoImportedCityAssignments.unresolvedAssignments.length > 0 && (
                          <Chip color="#ff6b6b" background="rgba(255,107,107,0.12)">
                            Catalogo: {prazoImportedCityAssignments.unresolvedAssignments.length} cidades sem match
                          </Chip>
                        )}
                      </div>

                      {prazoWorkbook && (
                        <>
                          <Row style={{ marginTop: "1rem" }}>
                            <Col lg="3" md="6" style={{ marginBottom: "0.9rem" }}>
                              <div style={fieldLabelStyle}>Aba</div>
                              <Input
                                type="select"
                                value={prazoImportMapping.sheetName}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  handlePrazoImportMappingChange("sheetName", value);
                                  if (value) {
                                    handlePreviewPrazoExcel(value);
                                  }
                                }}
                                style={compactInputStyle}
                              >
                                <option value="">Selecionar aba</option>
                                {prazoWorkbookSheets.map((sheet) => (
                                  <option key={sheet.name} value={sheet.name}>
                                    {sheet.name.trim()} ({sheet.rowCount} linhas)
                                  </option>
                                ))}
                              </Input>
                              <div style={{ color: "#8f9bc7", fontSize: "0.72rem", marginTop: "0.25rem" }}>
                                Cabecalho detectado na linha {(prazoWorkbook.headerRowIndex ?? 0) + 1}.
                              </div>
                            </Col>

                            {[
                              { key: "ufColumnIndex", label: "Coluna UF destino" },
                              { key: "cidadeColumnIndex", label: "Coluna cidade destino" },
                              { key: "rotaColumnIndex", label: "Coluna rota/regiao" },
                              { key: "prazoColumnIndex", label: "Coluna prazo" },
                            ].map((field) => (
                              <Col key={field.key} lg="2" md="6" style={{ marginBottom: "0.9rem" }}>
                                <div style={fieldLabelStyle}>{field.label}</div>
                                <Input
                                  type="select"
                                  value={prazoImportMapping[field.key]}
                                  onChange={(e) => handlePrazoImportMappingChange(field.key, e.target.value)}
                                  style={compactInputStyle}
                                >
                                  <option value="">Auto detectar</option>
                                  {prazoColumnOptions.map((option) => (
                                    <option key={`${field.key}-${option.index}`} value={option.index}>
                                      {option.label}{option.sampleValue ? ` :: ${option.sampleValue}` : ""}
                                    </option>
                                  ))}
                                </Input>
                              </Col>
                            ))}
                          </Row>

                          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.9rem" }}>
                            {prazoWorkbookSheets.map((sheet) => (
                              <Chip
                                key={`sheet-score-${sheet.name}`}
                                color={sheet.name === prazoWorkbook.selectedSheetName ? "#2ecc71" : "#8f9bc7"}
                                background={sheet.name === prazoWorkbook.selectedSheetName ? "rgba(46,204,113,0.12)" : "rgba(143,155,199,0.12)"}
                              >
                                {sheet.name.trim()}: score {sheet.score}
                              </Chip>
                            ))}
                          </div>
                        </>
                      )}

                      {(prazoPreviewSummary || prazoImportResult) && (
                        <div
                          style={{
                            marginTop: "0.8rem",
                            border: "1px solid #2b3553",
                            borderRadius: 8,
                            padding: "0.8rem 0.9rem",
                            background: "#131c30",
                          }}
                        >
                          <div style={{ color: "#f5f7fb", fontSize: "0.8rem", fontWeight: 600, marginBottom: "0.45rem" }}>
                            Resumo da leitura
                          </div>
                          <div style={{ color: "#aab6dd", fontSize: "0.78rem", lineHeight: 1.55 }}>
                            {prazoImportResult
                              ? `${prazoImportResult.matchedRows || 0} linhas foram aplicadas em rotas e ${prazoImportResult.unmatchedRows || 0} ficaram sem casamento no mapeamento de rota.`
                              : `${prazoPreviewSummary?.matchedRows || 0} linhas casaram com rotas na pre-validacao e ${prazoPreviewSummary?.unmatchedRows || 0} ainda precisam de ajuste.`}
                          </div>
                        </div>
                      )}

                      {prazoImportRouteSummary.length > 0 && (
                        <div style={{ marginTop: "0.9rem" }}>
                          <div style={{ ...fieldLabelStyle, marginBottom: "0.5rem" }}>Rotas afetadas</div>
                          <div style={{ display: "flex", gap: "0.45rem", flexWrap: "wrap" }}>
                            {prazoImportRouteSummary.slice(0, 12).map((item, index) => (
                              <Chip key={`prazo-route-${index}`} color="#2ecc71" background="rgba(46,204,113,0.12)">
                                {item.ufDestino} / {item.routeLabel}: {item.cityCount} cidades
                              </Chip>
                            ))}
                          </div>
                        </div>
                      )}

                      {prazoUnmatchedSamples.length > 0 && (
                        <div style={{ marginTop: "1rem" }}>
                          <div style={{ ...fieldLabelStyle, marginBottom: "0.5rem" }}>Linhas sem casamento de rota</div>
                          <div style={{ color: "#ffb1ab", fontSize: "0.77rem", lineHeight: 1.5 }}>
                            {prazoUnmatchedSamples.slice(0, 8).map((item, index) => (
                              <div key={`prazo-unmatched-${index}`}>
                                Linha {item.rowNumber}: {item.ufDestino || "-"} / {item.cidadeDestino || "-"} / {item.routeRaw || "-"}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {prazoImportedCityAssignments.unresolvedAssignments.length > 0 && (
                        <div style={{ marginTop: "1rem" }}>
                          <div style={{ ...fieldLabelStyle, marginBottom: "0.5rem" }}>Cidades da planilha nao encontradas no catalogo</div>
                          <div style={{ color: "#ffcf91", fontSize: "0.77rem", lineHeight: 1.5 }}>
                            {prazoImportedCityAssignments.unresolvedAssignments.slice(0, 8).map((item, index) => (
                              <div key={`prazo-city-unmatched-${index}`}>
                                {item.ufDestino || "-"} / {item.cidadeDestino || "-"} / {item.routeRaw || item.matchedRouteLabel || "-"}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>
              )}

              <div style={panelStyle}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap", marginBottom: "0.8rem" }}>
                  <div>
                    <div style={sectionTitleStyle}>JSON Estruturado</div>
                    <div style={{ color: "#8f9bc7", fontSize: "0.8rem" }}>
                      Este JSON e o alvo do MVP. A etapa seguinte so deve pensar em persistencia depois que essa estrutura estiver consistente.
                    </div>
                  </div>
                  <Button color="info" size="sm" onClick={handleCopyJson} disabled={!structuredJsonText}>
                    Copiar JSON
                  </Button>
                  {/* TASK-874: feedback OCR */}
                  <Button color="secondary" size="sm" outline onClick={handleSaveFeedback} disabled={!structuredJsonText || loadingFeedback}>
                    {loadingFeedback ? <Spinner size="sm" /> : "Salvar Feedback OCR"}
                  </Button>
                  <Button color="success" size="sm" onClick={handleSalvarTabela} disabled={!structuredJsonPreview || loadingSalvar}>
                    {loadingSalvar ? <Spinner size="sm" /> : "Salvar Tabela"}
                  </Button>
                </div>

                {savedTabelaId ? (
                  <AnexosTabela
                    tabelaId={savedTabelaId}
                    descricao={`Tabela ${savedTabelaId} salva. O PDF analisado foi anexado; adicione aqui o e-mail ou a planilha do carrier, se houver.`}
                  />
                ) : null}

                <Input
                  type="textarea"
                  readOnly
                  value={structuredJsonText}
                  rows="24"
                  style={{
                    background: "#131c30",
                    borderColor: "#2b3553",
                    color: "#dbe7ff",
                    fontSize: "0.76rem",
                    lineHeight: 1.45,
                  }}
                />
              </div>

              <div style={panelStyle}>
                <div style={sectionTitleStyle}>Texto Bruto Extraido</div>
                <Input
                  type="textarea"
                  readOnly
                  value={analysis?.rawTextPreview || ""}
                  rows="16"
                  style={{
                    background: "#131c30",
                    borderColor: "#2b3553",
                    color: "#dbe7ff",
                    fontSize: "0.77rem",
                    lineHeight: 1.45,
                  }}
                />
              </div>
            </>
          ) : (
            <div
              style={{
                border: "1px dashed #4c9dff",
                borderRadius: 10,
                padding: "1rem 1.1rem",
                background: "rgba(29, 140, 248, 0.08)",
              }}
            >
              <div style={{ color: "#dbe7ff", fontWeight: 600, marginBottom: "0.45rem" }}>
                Escopo deste menu
              </div>
              <div style={{ color: "#b9c8f2", fontSize: "0.84rem", lineHeight: 1.55 }}>
                Envie um PDF para gerar o rascunho inicial. Este menu foi criado para testar o novo fluxo documental separado do cadastro manual atual em <strong>/app/tabelas-frete</strong>.
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* TASK-875: Wizard de cadastro de transportadora */}
      <Modal isOpen={showWizard} toggle={() => setShowWizard(false)} modalClassName="modal-align-top" fade={false} size="md">
        <div
          style={{
            background: "#1b2238",
            borderBottom: "1px solid #2b3553",
            padding: "1rem 1.25rem 0.75rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ color: "#dbe7ff", fontWeight: 600 }}>Cadastrar Transportadora</div>
            <div style={{ color: "#8f9bc7", fontSize: "0.78rem" }}>
              Passo {wizardStep} de 3 — {wizardStep === 1 ? "Dados da transportadora" : wizardStep === 2 ? "Vínculo com embarcador" : "Tipo de serviço"}
            </div>
          </div>
          <button onClick={() => setShowWizard(false)} style={{ background: "none", border: "none", color: "#8f9bc7", fontSize: "1.1rem", cursor: "pointer" }}>✕</button>
        </div>

        <ModalBody style={{ background: "#1b2238" }}>
          {wizardStep === 1 && (
            <div>
              <FormGroup>
                <Label style={{ color: "#dbe7ff" }}>CNPJ</Label>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <Input
                    value={wizardForm.cnpj}
                    placeholder="00.000.000/0000-00"
                    onChange={(e) => { const v = e.target.value; setWizardForm((f) => ({ ...f, cnpj: v })); }}
                    style={{ flex: 1 }}
                  />
                  <Button color="info" size="sm" onClick={handleWizardCnpjLookup} disabled={loadingCnpj}>
                    {loadingCnpj ? <Spinner size="sm" /> : "Buscar"}
                  </Button>
                </div>
              </FormGroup>
              <FormGroup>
                <Label style={{ color: "#dbe7ff" }}>Razão Social</Label>
                <Input
                  value={wizardForm.razaoSocial}
                  onChange={(e) => { const v = e.target.value; setWizardForm((f) => ({ ...f, razaoSocial: v })); }}
                />
              </FormGroup>
              <FormGroup>
                <Label style={{ color: "#dbe7ff" }}>Nome Fantasia</Label>
                <Input
                  value={wizardForm.fantasia}
                  onChange={(e) => { const v = e.target.value; setWizardForm((f) => ({ ...f, fantasia: v })); }}
                />
              </FormGroup>
              <FormGroup>
                <Label style={{ color: "#dbe7ff" }}>CEP</Label>
                <Input
                  value={wizardForm.cep}
                  placeholder="00000000"
                  onChange={(e) => { const v = e.target.value.replace(/\D/g, ""); setWizardForm((f) => ({ ...f, cep: v })); }}
                />
              </FormGroup>
            </div>
          )}

          {wizardStep === 2 && (
            <div>
              <div style={{ color: "#8f9bc7", fontSize: "0.83rem", marginBottom: "0.75rem" }}>
                A transportadora será vinculada automaticamente ao embarcador atual:
              </div>
              <div
                style={{
                  padding: "0.85rem 1rem",
                  borderRadius: 8,
                  border: "1px solid #2b3553",
                  background: "rgba(76,157,255,0.08)",
                  color: "#dbe7ff",
                  fontSize: "0.88rem",
                }}
              >
                {embarcadorLabel}
              </div>
              <div style={{ color: "#8f9bc7", fontSize: "0.78rem", marginTop: "0.6rem" }}>
                Esta ação criará um registro em <code>EmbarcadorTransportadora</code>.
              </div>
            </div>
          )}

          {wizardStep === 3 && (
            <div>
              <div style={{ color: "#8f9bc7", fontSize: "0.83rem", marginBottom: "0.75rem" }}>
                Selecione o tipo de serviço para vincular à transportadora (opcional):
              </div>
              <FormGroup>
                <Label style={{ color: "#dbe7ff" }}>Tipo de Serviço</Label>
                <Input
                  type="select"
                  value={wizardSelectedTipoServico}
                  onChange={(e) => setWizardSelectedTipoServico(e.target.value)}
                >
                  <option value="">Selecionar depois</option>
                  {tiposServico.map((ts) => (
                    <option key={ts.id} value={ts.id}>{ts.nome}</option>
                  ))}
                </Input>
              </FormGroup>
              <div style={{ color: "#8f9bc7", fontSize: "0.78rem" }}>
                Transportadora: <strong style={{ color: "#dbe7ff" }}>{wizardForm.fantasia || wizardForm.razaoSocial}</strong>
              </div>
            </div>
          )}
        </ModalBody>

        <ModalFooter style={{ background: "#1b2238", borderTop: "1px solid #2b3553", gap: "0.5rem" }}>
          {wizardStep > 1 && (
            <Button color="secondary" size="sm" outline onClick={() => setWizardStep((s) => s - 1)}>
              Voltar
            </Button>
          )}
          <Button color="secondary" size="sm" onClick={() => setShowWizard(false)}>
            {wizardStep === 3 ? "Pular e fechar" : "Cancelar"}
          </Button>
          {wizardStep < 3 ? (
            <Button
              color="warning"
              size="sm"
              disabled={wizardStep === 1 && (!wizardForm.razaoSocial || !wizardForm.cnpj)}
              onClick={() => setWizardStep((s) => s + 1)}
            >
              Próximo
            </Button>
          ) : (
            <Button
              color="success"
              size="sm"
              disabled={loadingWizardSave}
              onClick={handleWizardSave}
            >
              {loadingWizardSave ? <Spinner size="sm" /> : "Salvar"}
            </Button>
          )}
        </ModalFooter>
      </Modal>
      </>
    </div>
  );
}
/* eslint-enable react-hooks/rules-of-hooks */

export default function NovaTabelaFrete() {
  const history = useHistory();
  const { addToast } = useToast();
  const { empresa, hasEmpresaSelection } = useCompanies();

  if (!hasEmpresaSelection) {
    return (
      <div className="content">
        <SpecificEmpresaRequiredNotice />
      </div>
    );
  }

  return (
    <NovaTabelaFreteContent
      history={history}
      addToast={addToast}
      empresa={empresa}
    />
  );
}
